/**
 * Shell **kasir** — satu dari beberapa peran di atas inti yang sama (K4).
 *
 * ### Prinsip antarmuka kasir: hitung KETUKAN, bukan fitur
 * Tambah satu item = **satu ketukan** pada kartu menu. Tak ada dialog, tak ada konfirmasi, tak ada
 * langkah "pilih jumlah" — jumlah bertambah dengan mengetuk kartu yang sama lagi. Riset lapangan
 * menyebut *taps to complete* sebagai keluhan operasional utama, dan tiap dialog yang ditambahkan
 * ke jalur ini berlaku pada **setiap item, setiap bill, setiap hari**.
 *
 * ### Yang sengaja BELUM ada, dan kenapa itu jujur
 * Belum ada modifier, combo, split bill, maupun diskon. Semuanya butuh keputusan yang belum
 * diambil, dan menambahkannya sekarang berarti menebak. Yang ada sekarang adalah jalur order-to-pay
 * yang **sudah terbukti benar sampai rupiah** terhadap bill nyata Arena.
 */
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HealthIndicator } from '../components/HealthIndicator';
import type { HealthState, HealthStatus } from '../components/HealthIndicator';
import { agentHealth, printViaAgent } from '../api/agent';
import type { AgentState } from '../api/agent';
import { api, pesanGalat } from '../api/client';
import { CACHE_KEYS, cacheGet, cacheSet } from '../api/cache';
import { adoptNextSeq, takeOrderNo } from '../api/order-no';
import { commitGuestName, peekGuestName } from '../api/guest-no';
import { openLive } from '../api/live';
import type { ApiError } from '../api/client';
import {
  assembleLocalKitchenTickets, assembleSale, drawerKickBytes, renderKitchenTicket, settleSale,
} from '@osbpos/domain';
import {
  promoMeetsMinimum, promoValidity,
} from '@osbpos/domain';
import type {
  AssembleCatalog, AssembledSale, KitchenTicketDoc, PromoSpec,
} from '@osbpos/domain';
import { addMoney, money, moneyToWire } from '@osbpos/contract';
import type { PrinterProfile } from '@osbpos/contract';

import { formatRupiah } from '../format';
import { hasPermission, logoutStaff, staffSession } from '../api/session';
import { enqueue, enqueueSale, flush, needsReviewCount, pendingCount } from '../api/outbox';
import { hariLuring } from '../api/client';
import type {
  Bill, ComboGroup, ComboSelectionInput, MenuItem, ModifierGroup, ModifierSelectionInput,
  ParkedOrder, PaymentMethod, SoldOutItem,
} from '../api/client';
import { AdminMenu } from './AdminMenu';
import { BillSearch } from './BillSearch';
import { ComboPicker } from './ComboPicker';
import { GuestCallPanel } from './GuestCallPanel';
import { LineContextBar } from './LineContextBar';
import { ModifierPicker } from './ModifierPicker';
import { ParkedList } from './ParkedList';
import { MenuBrowser } from './MenuBrowser';
import { TableDashboard } from './TableDashboard';
import { PrintButton } from './PrintButton';
import { RefundDialog } from './RefundDialog';
import { ReopenButton } from './ReopenButton';
import { PrinterSetup } from './PrinterSetup';
import { SalesToday } from './SalesToday';
import { TakeOverDialog } from './TakeOverDialog';
import type { HoldConflict } from './TakeOverDialog';
import { TenderPanel } from './TenderPanel';

export interface CashierProps {
  /**
   * ⛔ **Tak ada `tenantCode` di sini.** Tenant ada di dalam token (R-AI-1); layar tak pernah
   * mengetahuinya, apalagi mengirimkannya. Menaruhnya kembali sebagai prop berarti mengembalikan
   * jalur "tenant dari argumen" yang justru baru ditutup.
   */
  readonly outletCode: string;
  readonly deviceCode: string;
  /** Sesi kas berjalan. `null` = belum buka sesi ⇒ tombol laci nonaktif. */
  readonly shiftId?: string | null;
  /**
   * ⭐ **Tanggal usaha SESI**, bukan jam dinding. Sesi yang dibuka kemarin sore masih berjalan
   * pukul 01:30, dan transaksinya milik hari KEMARIN (§2) — kasir yang membaca tanggal hari ini
   * akan menandatangani setoran hari yang salah.
   */
  readonly businessDate?: string;
  /** ⭐ Tier penempatan dari SERVER (K13). Bawaan `server` — lihat catatan di `tablet`. */
  readonly tier?: 'server' | 'tablet';
  /**
   * ⭐⭐⭐ Profil printer perangkat ini (`GET /devices/me`) — dipakai **menendang laci di titik
   * tender**, bukan menunggu struk. ⚠️ `null`/`undefined` ⇒ lacinya tak pernah ditendang dari sini,
   * dan itu keadaan yang sah (terminal tanpa laci).
   */
  readonly printer?: unknown;
  readonly priceChannel?: string;
  /** Menu awal opsional (uji). Bila kosong, katalog dimuat dari API. */
  readonly menu?: readonly MenuItem[];
  /**
   * Pesanan yang sedang berjalan di layar ini — dilaporkan ke atas supaya tombol "← Meja"
   * bisa MELEPAS KUNCI sebelum pindah layar. Layar kasir sendiri tak tahu ada tombol itu.
   */
  readonly onOrderChanged?: (orderId: string | null) => void;
  /**
   * Membawa kasir ke layar sesi kas. Dipasang di menu ☰ — di mode quick order tak ada dashboard
   * meja, jadi tanpa jalan ini tutup sesi hanya bisa dicapai dengan me-reload browser.
   */
  readonly onSession?: () => void;
  /** Staf keluar (ganti orang) — sesi kas TIDAK ikut ditutup. */
  readonly onLoggedOut?: () => void;
}

/** ⚠️ Hanya untuk membedakan baris di LAYAR — bukan id, tak pernah dikirim ke server. */
let lineSeq = 0;

interface Line {
  /**
   * ⭐⭐ **Identitas baris di layar** — dan sejak set menu bisa disusun, ia WAJIB.
   *
   * ⛔ Sebelumnya `code` yang jadi kunci React, sasaran pilih, dan sasaran +/−/hapus. Selama satu
   * kode cuma bisa muncul sekali itu benar; begitu modifier lahir ia sudah rapuh, dan set menu
   * membuatnya jadi keadaan NORMAL: dua "Set Menu A" dengan nasi yang berbeda adalah dua baris
   * berkode sama. Akibatnya `increment('SET-A')` menaikkan **dua-duanya**, dan yang dihapus bisa
   * paket yang tak dipilih siapa pun — tanpa satu pun galat, cuma angka yang salah.
   */
  readonly key: string;
  readonly code: string;
  /**
   * ⭐ Id baris DI SERVER — terisi hanya untuk baris yang sudah tersimpan (mis. sesudah pesanan
   * ditahan lalu dipanggil kembali).
   *
   * ⛔ Inilah yang membuat "hapus" benar-benar menghapus: tanpa id, layar cuma bisa membuang baris
   * dari dirinya sendiri, dan barisnya tetap hidup di server lalu ikut tertagih.
   */
  readonly serverLineId?: string;
  readonly name: string;
  readonly price: string;
  qty: number;
  /**
   * ⭐ Modifier yang dipilih untuk baris ini.
   *
   * ⚠️ Ikut jadi bagian IDENTITAS baris: "Nasi Goreng pedas" dan "Nasi Goreng tak pedas" adalah
   * dua baris berbeda, bukan satu baris berjumlah dua. Menggabungkannya berarti dapur menerima
   * satu tiket untuk dua masakan yang berbeda.
   */
  readonly modifiers: readonly ModifierSelectionInput[];
  /** Label modifier untuk ditampilkan sebagai baris anak menjorok. */
  readonly modifierLabels: readonly string[];
  /**
   * ⭐⭐ Pilihan set menu — ADA berarti baris ini didorong lewat `addComboLine`, bukan `addLine`.
   *
   * ⛔ Baris ber-combo TAK PERNAH digabung dengan baris lain berkode sama: dua "Set Menu A" dengan
   * nasi yang berbeda adalah dua paket berbeda, dan menggabungkannya berarti dapur menerima satu
   * tiket untuk dua susunan yang tak sama. Alasannya identik dengan modifier di atas.
   */
  readonly combo?: readonly ComboSelectionInput[];
  /**
   * ⭐⭐ Berapa banyak dari baris ini yang **sudah sampai ke dapur**.
   *
   * Satu baris bisa separuh terkirim: dua porsi sudah di wajan, satu baru diketuk pelayan untuk
   * pesanan susulan. Menyimpannya sebagai angka — bukan boolean `sent` — membuat keadaan itu bisa
   * dinyatakan apa adanya, dan membuat batas penyuntingannya jelas: yang boleh dikurangi hanya
   * yang BELUM terkirim.
   */
  sentQty: number;
  /**
   * ⭐⭐⭐ Berapa banyak dari baris ini yang **sudah tersimpan sebagai `order_line` di server**.
   *
   * ⛔ SENGAJA terpisah dari `sentQty`. Keduanya sempat dianggap satu hal, dan itu menagih tamu dua
   * kali: `pushLines` mendorong `qty - sentQty`, sementara `sentQty` artinya "sudah sampai ke
   * DAPUR". Pesanan yang ditahan lalu dipanggil kembali punya baris yang **sudah ada di server**
   * tapi belum pernah ke dapur ⇒ `sentQty` 0 ⇒ seluruh isinya didorong LAGI sebagai baris baru.
   *
   * ⚠️ Gejalanya nol: bill di layar terlihat benar (layar memakai keadaannya sendiri), yang berlipat
   * cuma barisnya di server — dan itu baru terbaca saat tamu menerima strukmu.
   */
  serverQty: number;
}

/*
 * ⛔ **Kolom kanan baris = harga × jumlah, BUKAN harga satuan.**
 *
 * Sebelumnya kolom itu menampilkan harga satuan sementara subtotal menjumlahkan harga × jumlah.
 * Akibatnya baris `2× Nasi Goreng` tertulis `Rp 45.000`, dan kasir yang menjumlahkan kolom di
 * layar mendapat angka yang **tak akan pernah cocok** dengan subtotal di bawahnya. Itu jenis
 * kesalahan yang paling mahal: tak ada yang gagal, tak ada galat — hanya tamu yang bertanya
 * "kok tidak ketemu?" di depan kasir, dan kasir yang tak punya jawaban.
 */
/**
 * Nilai baris untuk DITAMPILKAN.
 *
 * ⚠️ `delta` = Σ tambahan modifier per satuan, dan ia WAJIB ikut: tanpa itu Es Kopi + Extra Shot
 * tampil Rp 25.000 sementara yang benar 33.000 — tamu membayar kurang tanpa satu pun gejala (G-2).
 */
function lineTotal(unitPrice: string, qty: number, delta = '0'): string {
  const satuan = Number(unitPrice.split('.')[0] ?? '0') + Number(delta.split('.')[0] ?? '0');
  return `${String(satuan * qty)}.0000`;
}

/**
 * Jeda poll status SYNC. ⚠️ Longgar dengan sengaja: jalur cepatnya dentuman WebSocket, dan poll ini
 * cuma jaring untuk saat socket-nya mati. Memendekkannya menambah beban tanpa menambah kecepatan.
 */
const SYNC_POLL_MS = 120_000;

/**
 * Berapa hari sebuah tanda HABIS sudah berdiri, dihitung dari **business date**.
 *
 * ⚠️ Bukan dari `marked_at`: outlet tutup pukul 02:00, dan tanda yang dipasang pukul 01:30 milik
 * hari SEBELUMNYA. Menghitung dari jam dinding akan menyebut "1 hari" untuk tanda yang baru
 * dipasang setengah jam lalu.
 */
function hariSejak(sejak: string, hariIni: string): number {
  if (sejak === '' || hariIni === '') return 0;
  const a = Date.parse(`${sejak}T00:00:00Z`);
  const b = Date.parse(`${hariIni}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * ⭐ **Sudah berapa lama data baru menunggu** — dipakai lencana & ajakan SYNC.
 *
 * ⚠️ Dibulatkan ke atas ke menit: "0 menit" terbaca sebagai "belum ada apa-apa", padahal justru
 * data yang baru saja tiba yang paling perlu ditarik.
 */
function menitSejak(iso: string | null): number | null {
  if (iso === null) return null;
  const t0 = Date.parse(iso);
  if (Number.isNaN(t0)) return null;
  return Math.max(1, Math.ceil((Date.now() - t0) / 60_000));
}

export function CashierShell(props: CashierProps) {
  const { outletCode, deviceCode } = props;
  const priceChannel = props.priceChannel ?? 'dine_in';
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  /** ⭐ Tiga tingkat: departemen → kategori → produk. `null` = "Semua" pada tingkat itu. */
  const [department, setDepartment] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [line, setLines] = useState<Line[]>([]);
  const [bill, setBill] = useState<Bill | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [server, setServer] = useState<HealthState>('checking');
  const [menu, setMenu] = useState<readonly MenuItem[]>(props.menu ?? []);
  const [paymentMethods, setPaymentMethods] = useState<readonly PaymentMethod[]>([]);
  /**
   * ⭐⭐ **Tier satu-tablet (K13)** — aturan pajak + versi katalog, dipegang utuh karena tablet
   * menghitung SENDIRI dengan `@osbpos/domain`.
   *
   * ⚠️ Tier adalah fakta **PENEMPATAN**, bukan data outlet: outlet yang sama bisa dilayani mini-PC
   * atau tidak sama sekali. Karena itu ia melekat pada perangkat (`osbpos.tier`), sama seperti
   * peran — bukan pada kapabilitas outlet.
   */
  const [taxRules, setTaxRules] = useState<AssembleCatalog['tax']>(undefined);
  const [catalogVersion, setCatalogVersion] = useState<string | undefined>(undefined);
  /*
   * ⭐⭐⭐ **Tier dari SERVER** (`GET /devices/me`), bukan dari `localStorage` — K13 dipindah ke DB
   * 2026-08-23. Sampai kini ia flag lokal, dan itu kesalahan yang sama persis dengan `role` sebelum
   * K21: manajer tak bisa menyetelnya, HQ tak bisa melihat terminal mana yang mode apa, dan siapa
   * pun yang membuka DevTools bisa mengubah **dari mana angka bill datang**.
   *
   * ⚠️ `localStorage` turun pangkat jadi override **`import.meta.env.DEV` saja** — satu tablet
   * menguji dua tier itu sah, tapi hanya di laptop. Persis nasib `?role=` di K21.
   */
  const tablet = props.tier === 'tablet'
    || (import.meta.env.DEV && localStorage.getItem('osbpos.tier') === 'tablet');
  /**
   * ⭐⭐⭐ Dokumen yang DITAGIHKAN — bukan dirakit ulang saat bayar.
   *
   * ⛔ Merakit ulang di tombol bayar berarti angka yang dikirim bisa berbeda dari angka yang
   * DILIHAT tamu, kalau katalognya kebetulan tersegarkan di antara keduanya. Yang masuk antrean
   * wajib hasil hitungan yang sama persis dengan yang ada di layar saat tamu setuju membayar.
   */
  const [assembled, setAssembled] = useState<AssembledSale | null>(null);
  /**
   * ⭐⭐ **Satu nomor per BILL, bukan per ketukan "Hitung Bill".**
   *
   * ⛔ Kasir menekan Hitung Bill berkali-kali (tambah item → hitung lagi). Mengambil nomor baru
   * tiap kali membakar deretan nomor untuk bill yang sama — celah nomor memang sah, tapi celah
   * yang lahir dari kebiasaan normal membuat deret nomor tak bisa dibaca siapa pun.
   *
   * ⚠️ Dikosongkan bersamaan dengan `assembled`: begitu penjualannya diantrekan atau dibatalkan,
   * bill berikutnya wajib nomor baru.
   */
  const nomorBill = useRef<string | null>(null);
  const [capabilities, setCapabilities] = useState<readonly string[]>([]);
  const [modifierGroups, setModifierGroups] = useState<readonly ModifierGroup[]>([]);

  /* ⚠️ Ditaruh TEPAT di bawah `modifierGroups` dengan sengaja: ia dipakai oleh nilai terhitung
     jauh di atas fungsi-fungsi lain, dan ditaruh di bawahnya layar kasir BLANK tanpa satu pun
     galat (TDZ). Jebakan yang sama sudah memakan waktu tiga kali di berkas ini. */
  /**
   * ⭐ Σ tambahan harga modifier PER SATUAN, dibaca dari katalog.
   *
   * ⛔ Dari KATALOG, bukan dari yang dikirim balik layar: harga tambahan yang boleh disebut layar
   * adalah harga yang bisa dikarang layar. Server tetap menghitung ulang dari masternya sendiri —
   * yang di sini cuma supaya angka di depan kasir benar sebelum tamu membayar.
   */
  const deltaModifier = useCallback((b: Line): string => {
    let total = money('0');
    for (const sel of b.modifiers) {
      const grup = modifierGroups.find((g) => g.groupCode === sel.groupCode);
      const opsi = grup?.options.find((o) => o.code === sel.optionCode);
      if (opsi !== undefined) total = addMoney(total, money(opsi.priceDelta));
    }
    return moneyToWire(total);
  }, [modifierGroups]);

  /** Produk yang sedang ditanyai modifiernya. `null` = tak ada dialog. */
  const [modFor, setModFor] = useState<MenuItem | null>(null);
  /** Grup pilihan set menu, dari katalog — layar tak pernah mengarang isi paket. */
  const [comboGroups, setComboGroups] = useState<readonly ComboGroup[]>([]);
  /**
   * ⭐⭐⭐ Promo yang ikut turun bersama katalog (2026-08-25) — inilah yang membuat potongan bisa
   * dihitung SAAT LURING. ⚠️ Kosong = katalog dari server lama; layar jatuh ke jalur daring.
   */
  const [promos, setPromos] = useState<readonly PromoSpec[]>([]);
  /** Set menu yang sedang disusun. `null` = tak ada; pandangan kembali ke menu. */
  const [comboFor, setComboFor] = useState<MenuItem | null>(null);
  /**
   * ⭐⭐ **Stok habis ("86")** — dua bentuk, sengaja terpisah.
   *
   * `soldOutCodes` cuma untuk MEREDUPKAN kartu; ia bisa datang dari katalog (termasuk salinan
   * luring), jadi ia selalu ada. `soldOutList` membawa siapa & sejak kapan, dan hanya lahir dari
   * jawaban server yang segar — pengingat "sudah 3 hari habis" tak boleh dihitung dari salinan
   * kemarin, karena angkanya akan salah persis pada hari orang mempercayainya.
   */
  const [soldOutCodes, setSoldOutCodes] = useState<ReadonlySet<string>>(new Set());
  const [soldOutList, setSoldOutList] = useState<readonly SoldOutItem[] | null>(null);
  /** ⭐ Opsi modifier yang habis — `<grup>/<opsi>`, karena kode opsi unik per GRUP (K28/0067). */
  const [soldOutMods, setSoldOutMods] = useState<ReadonlySet<string>>(new Set());
  /** Item yang sedang ditanyai "tandai habis?" lewat tekan-tahan. `null` = tak ada dialog. */
  const [holdFor, setHoldFor] = useState<MenuItem | null>(null);
  /** ⭐ Pengingat saat sesi kas dibuka — satu-satunya momen tanpa tamu menunggu. */
  const [soldOutReminder, setSoldOutReminder] = useState<readonly SoldOutItem[] | null>(null);
  const [saleMode, setSaleMode] = useState<string | null>(null);
  /** ⭐⭐⭐ Rute dapur + stasiun (K13 tier tablet) — dipakai mencetak tiket sendiri saat luring. */
  const [kitchen, setKitchen] = useState<{
    stations: readonly { code: string; name: string; kind: string; printer: unknown }[];
    routes: readonly { productCode: string; stationCode: string }[];
  } | null>(null);
  /** ⭐ `null` = katalog segar dari server; ISO = salinan, dan tanggalnya DITAMPILKAN. */
  const [catalogStale, setCatalogStale] = useState<string | null>(null);
  /**
   * ⭐⭐⭐ **Ada data baru yang DIKIRIM portal, dan terminal ini belum menariknya.**
   *
   * Bentuknya diambil dari VTEC (Imron, 2026-08-06): admin menekan Send Data, kasir dapat
   * pemberitahuan, lalu **kasir** yang menekan SYNC. ⛔ Sengaja bukan tarik-otomatis — harga yang
   * berganti sendiri di tengah antrean merampas kendali dari orang yang berdiri di depan tamu.
   *
   * ⚠️ Ini BEDA dari `catalogStale`: yang itu berarti "layar sedang pakai salinan karena luring",
   * yang ini "server punya yang lebih baru". Dua keadaan berbeda yang butuh dua kalimat berbeda —
   * menyatukannya membuat kasir luring mengira menekan SYNC akan menolong.
   */
  /**
   * ⭐⭐⭐ **Batas jualan luring** (Imron, 2026-08-25 — menutup keputusan terbuka #4:
   * *"batas jualan offline tenant setting. default 3 hari"*).
   *
   * ⚠️ Ini batas RISIKO, bukan batas teknis. Perangkat yang benar-benar luring memang tak bisa
   * ditegakkan server — yang menegakkan layar ini, dan itu disadari: yang dibeli adalah **outlet
   * yang berhenti menumpuk penjualan tak-terkirim diam-diam**, bukan kekebalan terhadap orang yang
   * sengaja mengakali tabletnya.
   */
  const [offlineMaxDays, setOfflineMaxDays] = useState(3);
  /**
   * ⭐⭐ **Umur keluringan dibaca BERKALA, bukan sekali saat render.**
   *
   * ⛔ Cacat yang ditutup ini nyata: till yang menyala terus (dan till memang menyala terus) tak
   * pernah me-render ulang karena waktu berlalu. Batas 3 hari yang cuma dihitung saat komponen
   * kebetulan render akan terlewat justru pada perangkat yang paling lama luring.
   *
   * ⭐ Bentuknya **pemicu render**, ⛔ bukan nilai yang disimpan di state. Umur keluringan dihitung
   * ULANG tiap render (`hariLuring()` di bawah), jadi tiap ketukan kasir sudah melihat angka
   * terbaru; timer ini cuma menjamin ada render walau layarnya tak disentuh siapa pun. Menyimpan
   * angkanya di state membuat ketukan melihat nilai yang berumur sampai dua menit.
   *
   * ⚠️ Menumpang timer poll yang sudah ada — dua timer untuk satu irama cuma menggandakan
   * permintaan di outlet yang jaringannya paling buruk.
   */
  const [tikLuring, setTikLuring] = useState(0);
  /** Berapa lama OUTLET terputus menurut mini-PC — 0 di tier tablet & saat dilayani portal. */
  const [outletOfflineDays, setOutletOfflineDays] = useState(0);
  const [syncPending, setSyncPending] = useState(false);
  /**
   * ⭐⭐ **Sejak kapan terminal ini tertinggal** — bukan sekadar tertinggal atau tidak.
   *
   * ⛔ Titik merah tak bisa dinilai; *"tertinggal 3 jam"* bisa. Yang tak bisa dinilai tak
   * mengubah perilaku siapa pun, dan lencana yang tak mengubah perilaku sama saja diam.
   */
  const [syncSince, setSyncSince] = useState<string | null>(null);
  /**
   * ⭐⭐⭐ **"Nanti" pada ajakan SYNC** (Imron, 2026-08-25: *"sync harus diketahui orang outlet.
   * jangan diam2"*).
   *
   * ⛔ Sengaja BUKAN penutup permanen: ia dibersihkan tiap kali layar kembali kosong, jadi ajakan
   * yang sama datang lagi di bill berikutnya. Pengingat yang bisa ditutup sekali selamanya adalah
   * pengingat yang ditutup sekali selamanya.
   */
  const [syncSnoozed, setSyncSnoozed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  /**
   * ⭐⭐ **Isi kolom kiri: MEJA atau MENU** — bukan dua layar (Imron, 2026-08-02).
   *
   * `'tables'` hanya mungkin di outlet `table_service`. Yang berganti cuma isi containernya;
   * bill, pita atas, dan panggilan tamu tetap di tempatnya, jadi berpindah meja tak pernah
   * membuat bill lenyap dari pandangan.
   */
  const [view, setView] = useState<'tables' | 'menu' | 'parked' | 'combo'>('menu');
  /** Pesanan yang sedang tertahan — dimuat saat layarnya dibuka, bukan terus-menerus. */
  const [parked, setParked] = useState<readonly ParkedOrder[]>([]);
  /** Dialog tanda: `null` = tertutup. */
  /**
   * Dialog penanda. `mode` membedakan dua perbuatan yang bentuk dialognya sama tapi akibatnya
   * berbeda: `park` MENAHAN (bill keluar dari layar), `tag` cuma memberi nama (bill tetap di sini).
   */
  const [tahanTanda, setTahanTanda] = useState<
    {
      mode: 'park' | 'tag'; kind: 'name' | 'queue' | 'table'; text: string; guests: string;
      /**
       * ⭐ Kotak mana yang langsung SIAP DIKETIK (Imron, 2026-08-25). ⛔ Bukan hiasan: kasir yang
       * cuma mau membetulkan jumlah tamu tetap mendarat di kotak nama kalau ini tak ada — lalu
       * mengetiknya ulang atau salah menimpanya.
       */
      focus?: 'label' | 'pax';
    } | null
  >(null);
  /**
   * ⭐⭐⭐ **Penanda pesanan yang sedang dikerjakan** — meja / antrean / nama + jumlah tamu.
   *
   * ⛔ Kenapa ini ada sama sekali: di KDS PB-PKB dua belas tiket berdiri berjudul nomor bill
   * (`UI/414821/1`) — nomor yang tak pernah diucapkan kasir maupun didengar tamu. Bagi juru masak
   * dua belas kartu tanpa penanda **adalah satu tumpukan**. (Imron, 2026-08-23.)
   */
  type Tanda = { kind: 'name' | 'queue' | 'table'; label: string; guests: number };
  const [tanda, setTandaState] = useState<Tanda | null>(null);
  /**
   * ⭐⭐⭐ **`ref` mendampingi state** (G-3, ditemukan UAT 2026-08-26) — pola yang sama persis
   * dengan `orderIdRef` di bawah, dan lahir dari sebab yang sama.
   *
   * ⛔ `simpanTanda` menyimpan penandanya lalu **langsung** menjalankan lanjutannya (kirim/bayar)
   * di tick yang SAMA. Lanjutan itu closure dari render SEBELUMNYA, jadi `pushLines` di dalamnya
   * masih melihat `tanda === null` — dan baris penyusul `tagOrder` di ujung `pushLines` dilewati
   * begitu saja. Akibatnya: layar menulis "NAMA · Budi", server menyimpan `park_kind` NULL, dan
   * dapur menerima kartu tanpa nama. Persis keadaan yang K31 lahir untuk mencegahnya.
   *
   * ⚠️ Menunggu render berikutnya bukan jalan keluar: yang dibutuhkan nilai yang sudah benar
   * SEBELUM render itu datang.
   */
  const tandaRef = useRef<Tanda | null>(null);
  /** ⛔ SATU pintu: menulis state saja meninggalkan `ref` basi, dan itu bug yang sama lahir lagi. */
  const setTanda = useCallback((t: Tanda | null) => {
    tandaRef.current = t;
    setTandaState(t);
  }, []);
  /** Yang harus dilanjutkan sesudah penandanya terisi (kirim ke dapur / bayar). */
  const lanjutSesudahTanda = useRef<(() => void) | null>(null);
  /**
   * ⭐⭐ Tanda yang SUDAH melekat pada pesanan ini — diisi saat pesanan dipanggil kembali.
   *
   * ⚠️ `orderId`-nya ikut disimpan, dan itu bukan hiasan: dengan begitu tanda ini **kedaluwarsa
   * sendiri** begitu pesanannya berganti atau ditutup. Menyimpannya terpisah menuntut seseorang
   * ingat mengosongkannya di keempat tempat `setOrderId(null)` dipanggil — dan yang kelak menambah
   * tempat kelima tak akan tahu ia harus ikut membersihkan.
   */
  const [tandaTersimpan, setTandaTersimpan] =
    useState<{ orderId: string; kind: 'name' | 'queue' | 'table'; label: string } | null>(null);
  /** Penjaga: pandangan awal ditentukan SEKALI, bukan tiap katalog dimuat ulang. */
  const viewDecided = useRef(false);
  /** Meja yang sedang dilayani — kosong di quick service. */
  const [table, setTable] = useState<{ code: string; guests: number } | null>(null);
  const [orderId, setOrderIdState] = useState<string | null>(null);
  /**
   * ⭐⭐⭐ **Cermin `orderId` yang bisa dibaca DALAM SATU TICK.**
   *
   * ⛔ Ini menutup bug yang terjadi di server live 2026-08-23: satu ketukan **Bayar** di
   * quick_service melahirkan **DUA pesanan**. Sebabnya bukan balapan jaringan melainkan React —
   * `bayarLangsung` merangkai `sendOrder()` lalu `computeBill()`, keduanya closure dari render yang
   * SAMA. `sendOrder` membuat pesanan A dan memanggil `setOrderId(A)`, tapi state itu baru terlihat
   * di render BERIKUTNYA; `computeBill` masih membaca `orderId === null` ⇒ membuat pesanan B.
   *
   * Akibatnya persis jenis kegagalan yang paling mahal: barisnya masuk ke A dan **terkirim ke
   * dapur** (tiketnya bahkan tercetak), sementara yang ditagih B — yang barisnya tak pernah
   * dikirim. Server menolak dengan benar (`ORDER-409-STATE`, "1 baris belum dikirim ke dapur"),
   * dan yang dilihat kasir cuma tombol Bayar yang menolak terus sementara dapur sudah memasak.
   *
   * ⚠️ `ref`, bukan state, karena yang dibutuhkan adalah nilai yang **sudah benar sebelum render
   * berikutnya** — pola yang sama dengan `lineSeq` di berkas ini.
   */
  const orderIdRef = useRef<string | null>(null);
  /** ⛔ SATU pintu: menulis state saja meninggalkan `ref` basi, dan itu bug yang sama lahir lagi. */
  const setOrderId = useCallback((id: string | null) => {
    orderIdRef.current = id;
    setOrderIdState(id);
  }, []);
  const [paidAmount, setPaidAmount] = useState('0.0000');
  const [done, setDone] = useState(false);
  /**
   * ⭐⭐ **Bill lunas yang baru saja ditutup di terminal ini** — satu-satunya yang boleh dibuka
   * kembali dari layar kasir.
   *
   * ⛔ Sengaja BUKAN pencari bill lunas. Kasus yang dibenarkan `reopenOrder` adalah salah catat
   * yang **tamunya masih berdiri di depan kasir**; daftar bill lunas yang bisa ditelusuri
   * mengubahnya jadi wewenang membongkar penjualan siapa pun, kapan pun — dan itu wewenang yang
   * tak seorang pun pernah minta.
   */
  const [lastPaidId, setLastPaidId] = useState<string | null>(null);
  /**
   * ⭐⭐⭐ Bill lunas yang sedang di-refund. `null` = tak ada dialog.
   *
   * ⛔ Refund berdiri BERDAMPINGAN dengan "buka kembali", bukan menggantikannya — keduanya menjawab
   * pertanyaan yang berbeda. Buka-kembali membetulkan **pencatatan** (salah pilih tender, diskon
   * terlewat) selagi tamunya masih di depan kasir; refund mengembalikan **uang** yang memang harus
   * kembali. Menyatukan keduanya berarti tiap salah ketik melahirkan dua peristiwa ekonomi palsu.
   */
  const [refundFor, setRefundFor] = useState<string | null>(null);
  /** ⭐ Layar cari bill terbuka? Jalur masuk refund untuk bill yang BUKAN transaksi terakhir. */
  const [findOpen, setFindOpen] = useState(false);
  /** ⭐ "Apa yang laku hari ini" — pertanyaan yang BEDA dari X-report (itu soal isi laci). */
  const [salesOpen, setSalesOpen] = useState(false);
  /** ⭐ Pilih printer dari yang AGEN lihat — supaya MAC tak perlu diketik siapa pun. */
  const [printerOpen, setPrinterOpen] = useState(false);
  /**
   * ⭐⭐⭐ **Panel pesanan sebagai LEMBAR di HP tegak** (Imron, 2026-08-25: *"tampilan tender di hp
   * vertical berantakan… buat floating button utk on off order container"*).
   *
   * ### ⛔ Kenapa lembar, bukan menukar kiri↔kanan
   * `.cashier` adalah `1fr 380px`. Di layar 393px, menukar sisinya tetap menyisakan ~190px untuk
   * masing-masing — kisi menu jadi satu kolom sempit DAN daftar baris terpenggal. Yang dibutuhkan
   * bukan menggeser tempat, melainkan **satu tampil pada satu waktu**.
   *
   * ⚠️ Keadaan ini hanya berarti di HP; di tablet & desktop CSS mengabaikannya dan panelnya tetap
   * berdiri di kanan. ⛔ Karena itu ia TIDAK dipagari `window.innerWidth` di JS: lebar layar yang
   * dibaca sekali saat render akan salah begitu tablet diputar.
   */
  const [sheetOpen, setSheetOpen] = useState(false);
  /**
   * Ringkasan untuk tombol mengambang. ⚠️ Total BERJALAN dari harga baris — ⛔ bukan pengganti
   * `bill.grandTotal`: yang ini belum kena pajak & service charge, dan ia memang tak boleh
   * dipakai menagih. Ia menjawab *"itemnya sudah masuk?"*, bukan *"berapa yang dibayar?"*.
   */
  const jumlahItem = line.reduce((n, b) => n + b.qty, 0);
  const totalBerjalan = line
    .reduce((acc, b) => acc + Number(lineTotal(b.price, b.qty, deltaModifier(b))), 0)
    .toFixed(4);
  const [refundInfo, setRefundInfo] = useState<string | null>(null);
  /** Bill terkunci orang lain: `null` = tak ada bentrok. Dijawab dialog paksa-buka. */
  const [holdConflict, setHoldConflict] = useState<HoldConflict | null>(null);
  /**
   * ⭐ Baris yang sedang dipilih — inilah yang membuat bar konteks punya sasaran.
   * `null` = tak ada; bar tak muncul, dan tak ada fungsi baris yang bisa salah sasaran.
   */
  const [selected, setSelected] = useState<string | null>(null);
  /** Penjualan yang menunggu diakui portal — WAJIB terlihat, tak boleh diam-diam. */
  const [queued, setQueued] = useState(() => pendingCount());
  /** ⛔ Menunggu MANUSIA, bukan jaringan — dan karena itu tak boleh dicampur dengan `queued`. */
  const [review, setReview] = useState(() => needsReviewCount());
  /**
   * ⭐ Promo yang sudah terpasang — ditampilkan supaya kasir tak menerapkannya dua kali.
   * ⚠️ Server tetap menolak yang kedua (`PROMO-409-DUPLICATE`); layar hanya membuat penolakan itu
   * tak perlu terjadi di depan tamu.
   */
  /**
   * ⭐⭐⭐ **Promo yang dipilih di tier tablet** — dipegang di layar, dihitung `assembleSale`.
   * ⛔ `null` di tier server: di sana potongannya milik pesanan yang hidup di server.
   *
   * ⚠️ Dideklarasikan DI SINI bersama state lain, ⛔ bukan di dekat `applyPromo`. `computeBill`
   * membacanya, dan `computeBill` berdiri jauh di atas — daftar dependensinya dievaluasi saat
   * render, jadi deklarasi yang lebih bawah membuat seluruh layar kasir **jatuh ke halaman kosong**
   * (TDZ), tanpa satu pun galat yang terlihat di layar.
   */
  const [promoTablet, setPromoTablet] = useState<PromoSpec | null>(null);
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  /**
   * ⭐⭐ Promo bertanda `requires_approval` — server menolaknya sekali, dan penolakan itulah yang
   * memunculkan kotak penyelia. `null` = belum diminta.
   *
   * ⚠️ Besar potongannya disimpan supaya bisa DISEBUT di kotak persetujuan: penyelia yang menekan
   * PIN tanpa tahu berapa rupiah yang disetujui hanya menandatangani kertas kosong.
   */
  const [approvalNeeded, setApprovalNeeded] = useState<{ discountTotal: string } | null>(null);
  const [approvalCode, setApprovalCode] = useState('');
  const [approvalPin, setApprovalPin] = useState('');
  const [promoInfo, setPromoInfo] = useState<string | null>(null);
  /**
   * ⭐⭐ **Pemberitahuan dapur** — produk yang tak punya tujuan rute (K17).
   *
   * ⛔ Dulu ia menumpang `promoInfo`, dan itu bug yang senyap: satu state memikul DUA pesan yang
   * tak berhubungan, jadi konfirmasi promo tertimpa pemberitahuan rute (atau sebaliknya) tanpa
   * satu pun galat. Ketahuan 2026-08-25 dari uji promo-luring yang membaca kotak KOSONG.
   */
  const [kitchenNotice, setKitchenNotice] = useState<string | null>(null);

  /* ── katalog dari API — harga & metode bayar datang dari SERVER, tak pernah dikarang ── */

  /**
   * ⭐⭐ Dipisah jadi fungsi supaya bisa dipanggil TIGA jalur: saat layar dimuat, saat kasir
   * menekan **SYNC**, dan otomatis saat **sesi kas dibuka**.
   *
   * ⚠️ `token` menjaga jawaban lama tak menimpa yang baru: kasir yang berganti kanal harga dua kali
   * cepat bisa menerima balasan permintaan pertama SESUDAH yang kedua, dan menu yang muncul jadi
   * menu kanal yang salah — tanpa satu pun galat.
   */
  const muatToken = useRef(0);
  const muatKatalog = useCallback(async (): Promise<void> => {
    muatToken.current += 1;
    const token = muatToken.current;
    const pakai = (c: Awaited<ReturnType<typeof api.catalog>>) => {
      if ((props.menu ?? []).length === 0) setMenu(c.items);
      setPaymentMethods(c.paymentMethods);
      setTaxRules(c.tax);
      setCatalogVersion(c.version);
      setCapabilities(c.capabilities ?? []);
      setModifierGroups(c.modifierGroups ?? []);
      setComboGroups(c.comboGroups ?? []);
      setPromos(c.promos ?? []);
      setSaleMode(c.saleMode ?? null);
      /* ⭐ Batas jualan luring (keputusan #4) + berapa lama OUTLET ini terputus menurut mini-PC.
         ⚠️ Ikut disimpan di salinan katalog, jadi ia tetap diketahui saat perangkat luring —
         batas yang cuma diketahui saat daring tak menjaga apa pun. */
      setOfflineMaxDays(c.offlineMaxDays ?? 3);
      setOutletOfflineDays(c.outletOfflineDays ?? 0);
      /* ⚠️ Hanya untuk meredupkan kartu. Daftar bernyawa (siapa/sejak kapan) datang dari
         `muatSoldOut`; katalog salinan tak boleh dipakai menghitung "sudah N hari". */
      setSoldOutCodes(new Set(c.soldOut ?? []));
      setSoldOutMods(new Set(c.soldOutModifiers ?? []));
      /* ⭐ Rute dapur ikut disimpan — tier tablet memakainya untuk mencetak tiket SENDIRI saat
         luring, dan ia ikut ke salinan katalog supaya masih ada saat internet putus. */
      setKitchen(c.kitchen ?? null);
      /*
       * ⛔⛔ **SEKALI saja, saat katalog pertama datang.**
       *
       * Ini berjalan ulang tiap katalog dimuat ulang — termasuk saat kasir sekadar mengganti
       * bahasa atau menekan SYNC. Tanpa penjaga ini, pandangan melompat kembali ke daftar meja di
       * tengah pesanan yang sedang diketik: bukan galat, tak ada pesan, cuma pekerjaan yang
       * hilang. (Ketahuan dari uji K7 yang tiba-tiba merah, bukan dari membaca kode.)
       */
      if (!viewDecided.current) {
        viewDecided.current = true;
        if (c.saleMode === 'table_service') setView('tables');
      }
    };

    try {
      /*
       * ⛔ Tanggal usaha SESI, bukan jam tablet — harga punya `valid_from`/`valid_to`, jadi
       * tanggal yang meleset satu hari mengembalikan katalog dengan harga hari yang salah.
       */
      const c = await api.catalog(outletCode, priceChannel, props.businessDate ?? '');
      if (token !== muatToken.current) return;
      /*
       * ⭐⭐ Salinan bertahan (K13): tablet yang MENYALA luring harus punya menu. Yang disimpan
       * jawaban server apa adanya — termasuk aturan pajak & `version`, karena tanpa keduanya
       * `assembleSale` MELEMPAR, dan itu benar: menghitung "seadanya" menghasilkan bill tanpa
       * pajak yang terlihat wajar.
       */
      cacheSet(CACHE_KEYS.catalog(outletCode, priceChannel), c);
      setCatalogStale(null);
      /* ⭐ Mengunduh katalog ITULAH "apply" — server mencatatnya, jadi lencananya padam di sini. */
      setSyncPending(false);
      pakai(c);
    } catch {
      /*
       * ⛔⛔ LURING: pakai salinan terakhir, dan **KATAKAN** bahwa ini salinan.
       *
       * Menampilkan menu kemarin tanpa tanda membuat kasir menjual harga yang mungkin sudah
       * berubah tanpa pernah tahu — dan yang menemukannya adalah tamu yang membandingkan struk
       * dengan daftar harga di dinding. Salinan yang diakui jauh lebih baik daripada layar kosong
       * ATAU salinan yang menyamar jadi mutakhir.
       */
      const simpan = cacheGet<Awaited<ReturnType<typeof api.catalog>>>(
        CACHE_KEYS.catalog(outletCode, priceChannel),
      );
      if (simpan === undefined) { setError(t('cashier.loadFailed')); return; }
      if (token !== muatToken.current) return;
      pakai(simpan.value);
      setCatalogStale(simpan.savedAt);
    }
  }, [outletCode, priceChannel, props.menu, props.businessDate, t]);

  useEffect(() => { void muatKatalog(); }, [muatKatalog]);

  /**
   * ⭐⭐ Dipisah jadi fungsi supaya bisa dipanggil dari TIGA jalur: saat layar dimuat, **sesudah
   * menahan**, dan saat ada dentuman `parked` dari terminal lain.
   *
   * ⛔ Sebelum ini ia cuma efek ber-dependensi `orderId` — jadi daftarnya bergantung pada `orderId`
   * yang kebetulan berubah. Menahan memang mengubahnya (`id → null`), tapi menyandarkan kesegaran
   * daftar pada efek samping variabel lain adalah kebetulan, bukan aturan: begitu urutannya
   * bergeser, kasir menekan Tahan dan billnya lenyap tanpa muncul di mana pun.
   *
   * ⚠️ `''` ikut ditolak, bukan cuma `undefined`/`null`: bertanya dengan tanggal kosong
   * mengembalikan daftar kosong SELAMANYA — dan kosong yang salah tak bisa dibedakan dari kosong
   * yang benar.
   */
  const muatTertahan = useCallback(async (): Promise<void> => {
    const bd = props.businessDate;
    if (bd === undefined || bd === null || bd === '') return;
    try {
      setParked(await api.parked(outletCode, bd));
    } catch {
      /* ⚠️ Diam: daftar yang cuma informatif tak boleh mengganggu bill yang sedang dikerjakan. */
    }
  }, [outletCode, props.businessDate]);

  useEffect(() => { void muatTertahan(); }, [muatTertahan, orderId]);

  /**
   * ⭐ Menarik daftar stok habis — ⛔ **otomatis, tanpa menunggu SYNC**.
   *
   * K25 menahan katalog di balik tombol karena harga yang berganti sendiri di tengah antrean
   * merampas kendali dari kasir. Tanda habis tak mengubah satu rupiah pun; ia cuma mencabut
   * pilihan yang barangnya memang sudah tak ada. Menahannya di balik tombol berarti terminal
   * sebelah tetap menjual ayam bakar yang habis sampai kasirnya kebetulan menekan SYNC.
   */
  const muatSoldOut = useCallback(async (): Promise<readonly SoldOutItem[] | null> => {
    try {
      const r = await api.soldOut(outletCode);
      setSoldOutList(r.items);
      /* ⚠️ Dua sasaran dipisah kembali di sini: kartu menu & opsi modifier diredupkan oleh dua
         himpunan berbeda, dan mencampurnya akan meredupkan produk bernama sama dengan opsinya. */
      setSoldOutCodes(new Set(
        r.items.filter((x) => x.kind !== 'modifier').map((x) => x.productCode)));
      setSoldOutMods(new Set(
        r.items.filter((x) => x.kind === 'modifier').map((x) => x.productCode)));
      return r.items;
    } catch {
      /* ⚠️ Diam: luring bukan alasan mengganggu kasir. Kartu tetap redup memakai kode dari
         katalog salinan — yang hilang cuma kemutakhirannya, bukan tandanya. */
      return null;
    }
  }, [outletCode]);

  useEffect(() => { void muatSoldOut(); }, [muatSoldOut]);

  /**
   * ⭐⭐⭐ **Apakah portal sudah mengirim data baru?** — sumber tombol SYNC.
   *
   * Dua jalur, dan keduanya perlu: dentuman `catalog` lewat WebSocket (cepat), dan poll berkala
   * (jaring, untuk saat socket-nya mati). ⛔ Degradasi kanal dorong WAJIB TERLIHAT (K5) — di sini
   * ia terlihat sebagai keterlambatan beberapa menit, bukan sebagai pemberitahuan yang HILANG.
   */
  const periksaSync = useCallback(async (): Promise<void> => {
    try {
      const st = await api.catalogStatus(outletCode);
      setSyncPending(st.stale);
      setSyncSince(st.stale ? st.publishedAt : null);
    } catch {
      /* ⚠️ Diam: gagal memeriksa status bukan alasan mengganggu kasir. Yang luring sudah punya
         lencana sendiri (`catalogStale`), dan dua peringatan untuk satu keadaan malah membingungkan. */
    }
  }, [outletCode]);

  useEffect(() => {
    void periksaSync();
    const t = setInterval(() => {
      setTikLuring((n) => n + 1);
      void periksaSync();
      /* ⚠️ Menumpang timer yang sama: dua timer untuk satu irama cuma menggandakan permintaan di
         outlet yang jaringannya paling buruk. */
      void muatSoldOut();
    }, SYNC_POLL_MS);
    return () => { clearInterval(t); };
  }, [periksaSync, muatSoldOut]);

  /**
   * ⭐⭐⭐ **Sesi kas dibuka ⇒ tarik katalog otomatis** (Imron, 2026-08-06).
   *
   * Inilah satu-satunya momen di mana menarik data TANPA ditanya itu aman dan benar: belum ada
   * satu bill pun, kasir memang sedang menyiapkan mesinnya, dan tak ada tamu yang menunggu. Sisa
   * harinya tetap manual lewat SYNC — persis alur VTEC.
   *
   * ⛔ Yang dijaga: kasir yang lupa menekan SYNC seharian tetap membuka hari berikutnya dengan
   * data mutakhir. Tanpa ini, terminal bisa tertinggal berminggu-minggu tanpa satu pun momen
   * yang memaksanya menyusul.
   *
   * ⚠️ Dipicu oleh PERPINDAHAN `null → ada`, bukan oleh `shiftId` yang sekadar ada: efek yang
   * berjalan tiap render dengan sesi terbuka akan menarik katalog berkali-kali sehari.
   */
  const sesiSebelumnya = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const sekarang = props.shiftId ?? null;
    const dulu = sesiSebelumnya.current;
    sesiSebelumnya.current = sekarang;
    if (dulu === undefined) {
      /*
       * ⛔⛔⛔ **Pengingat stok habis PERNAH MATI TOTAL** (FB-86-04, UAT 2026-08-29).
       *
       * `CashierShell` hanya dipasang SESUDAH sesi kas ada (`App.tsx`: `shiftId === null` ⇒
       * `SessionShell`). Jadi perpindahan `null → ada` yang dijaga di bawah **tak pernah terjadi
       * di dalam komponen ini**: saat ia lahir, `shiftId` sudah terisi, dan render pertama justru
       * yang dilewati. Membuka sesi kas lalu kembali pun tak menolong — pindah ke `SessionShell`
       * MELEPAS komponen ini, jadi `ref`-nya kembali `undefined`.
       *
       * ⇒ Pengingatnya ada, diuji lewat layar, dan tak pernah muncul sekali pun.
       *
       * ⚠️ Yang dipanggil di sini HANYA pengingatnya, bukan `muatKatalog()`: tarik-otomatis K25
       * memang milik perpindahan sesi, dan memanggilnya tiap kali komponen dipasang akan menarik
       * katalog tiap muat ulang halaman — persis yang catatan di atas hendak cegah.
       */
      if (sekarang !== null) {
        void muatSoldOut().then((items) => {
          if (items !== null && items.length > 0) setSoldOutReminder(items);
        });
      }
      return; // render pertama: katalog sudah dimuat efeknya sendiri
    }
    if (dulu === null && sekarang !== null) {
      void muatKatalog();
      /*
       * ⭐⭐ **Pengingat stok habis** — tandanya bertahan lintas hari (Imron, 2026-08-23), jadi
       * inilah momen yang harus mengatakannya: belum ada bill, tak ada tamu menunggu, dan orang
       * yang membuka sesi memang sedang menyiapkan mesinnya.
       *
       * ⛔ Tanpa ini, satu-satunya gejala item yang mati berhari-hari adalah penjualan yang hilang
       * — dan penjualan yang hilang tak punya baris di laporan mana pun.
       */
      void muatSoldOut().then((items) => {
        if (items !== null && items.length > 0) setSoldOutReminder(items);
      });
    }
  }, [props.shiftId, muatKatalog, muatSoldOut]);

  useEffect(() => {
    const staf = staffSession();
    if (staf === undefined) return;
    const kanal = openLive({
      outletCode,
      staffToken: staf.token,
      /* ⚠️ Dentuman tak membawa isi — ia cuma memicu PEMERIKSAAN, bukan penarikan katalog.
         Yang menarik tetap kasir lewat SYNC. */
      onNudge: (kind) => {
        if (kind === undefined || kind === 'catalog') void periksaSync();
        /* ⛔ Yang ini DITARIK, bukan sekadar ditandai — lihat catatan di `muatSoldOut`. */
        if (kind === undefined || kind === 'sold_out') void muatSoldOut();
        /* ⭐ Terminal LAIN menahan/memanggil pesanan ⇒ daftar di sini ikut menyusul. Tanpa ini
           dua till melihat daftar tertahan yang berbeda, dan yang kedua memanggil bill yang sudah
           diambil orang. */
        if (kind === undefined || kind === 'parked') void muatTertahan();
      },
      /* ⚠️ Keadaan kanal sudah punya lencananya sendiri di layar ini (`HealthIndicator`); yang di
         sini cuma pelanggan kedua atas socket yang sama, jadi ia tak perlu ikut menampilkannya. */
      onState: () => undefined,
    });
    return () => { kanal.close(); };
  }, [outletCode, periksaSync, muatSoldOut, muatTertahan]);

  /* ── kesehatan: polling ringan; printer sengaja `unknown` sampai device-agent ada ── */
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const startedAt = performance.now();
      try {
        const r = await api.ready();
        if (!alive) return;
        const ms = performance.now() - startedAt;
        setServer(r.status === 'ready' ? (ms > 800 ? 'slow' : 'healthy') : 'down');
      } catch {
        if (alive) setServer('down');
      }
    };
    void check();
    const id = setInterval(() => void check(), 10_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  /*
   * ⭐ Antrean didorong **menumpang denyut kesehatan**, bukan sekali saat masuk antrean.
   *
   * ⛔ Sebagai efek yang bergantung pada `[server, queued]`, ia hanya berjalan sekali: percobaan
   * gagal tak mengubah kedua nilai itu, jadi antrean menggantung sampai kasir menutup transaksi
   * berikutnya. Menumpang denyut membuat percobaannya **berulang dengan sendirinya** — dan
   * denyutnya sudah membuktikan server menjawab, jadi ia justru saat yang paling tepat.
   */
  useEffect(() => {
    if (queued === 0) return;
    let alive = true;
    const push = () => {
      void flush().finally(() => {
        if (!alive) return;
        setQueued(pendingCount());
        setReview(needsReviewCount());
      });
    };
    push();
    const id = setInterval(push, 5_000);
    return () => { alive = false; clearInterval(id); };
  }, [queued]);

  /**
   * ⭐⭐ **Lencana printer akhirnya menjawab sesuatu** (Imron, 2026-08-22).
   *
   * ⛔ Sampai hari ini ia dipaku `unknown` dengan komentar *"device-agent belum ada"* — dan itu
   * BENAR saat ditulis, tapi agennya jadi 2026-08-10 (K27, `osbpos-agent` di `127.0.0.1:9110`,
   * lengkap dengan `GET /agent/health`). `agentHealth()` di klien pun sudah ada dan tak pernah
   * dipanggil. ⚠️ Lencana yang selamanya abu-abu tak lebih baik daripada lencana yang berbohong:
   * dua-duanya membuat orang berhenti membacanya.
   *
   * ### ⚠️ Yang dijawab lencana ini, dan yang TIDAK
   * Hijau = **jalur cetak dari terminal ini hidup** (agen menjawab). ⛔ BUKAN janji bahwa kertasnya
   * ada atau printernya menyala — itu hanya terbukti dari mencoba mencetak, dan `PrintButton`
   * sudah membedakan "tercatat tapi belum tercetak" dari "gagal" (K27).
   *
   * ⛔ Tak-menjawab dipetakan ke `down`, ⛔ bukan `unknown`. Ini bukan ketidaktahuan: `pos-web`
   * mencetak HANYA lewat agen lokal (`printViaAgent`) apa pun transportnya, jadi agen yang diam
   * berarti terminal ini memang tak bisa mencetak — pernyataan yang lebih berguna daripada abu-abu.
   */
  const [printer, setPrinter] = useState<HealthState>('checking');
  const [printerState, setPrinterState] = useState<AgentState>('down');
  /**
   * Hasil cetak struk OTOMATIS sesudah lunas. ⚠️ Tiga keadaan, bukan dua: "tercatat tapi kertas
   * belum keluar" wajib bisa dibedakan dari "gagal", karena yang pertama tak menolong bila ditekan
   * lagi (`printCount` naik untuk kertas yang tak pernah ada).
   */
  const [autoPrint, setAutoPrint] =
    useState<{ kind: 'idle' | 'busy' | 'ok' | 'warn'; message?: string }>({ kind: 'idle' });
  useEffect(() => {
    let alive = true;
    const cek = () => {
      void agentHealth().then((st) => {
        if (!alive) return;
        /*
         * ⛔⛔ TIGA keadaan, bukan dua. Agen yang hidup tanpa printer ter-pair adalah keadaan
         * PALING SERING saat pemasangan pertama — aplikasinya sudah dipasang, printernya belum —
         * dan memetakannya jadi hijau berarti lencana berbohong tepat di situ. Kegagalannya lalu
         * muncul di tamu pertama yang minta struk.
         *
         * ⚠️ `slow` (kuning) dipilih, bukan `down` (merah): jalur cetaknya memang ada dan agennya
         * menjawab; yang kurang printernya. Merah untuk keduanya membuat kasir tak bisa
         * membedakan "pasang aplikasi" dari "pair printer" — dua pekerjaan yang berbeda.
         */
        /* ⚠️ `rejected` kuning, bukan merah: agennya ADA dan menjawab — yang salah setelannya. */
        setPrinter(st === 'ready' ? 'healthy'
          : st === 'no-printer' || st === 'rejected' ? 'slow' : 'down');
        setPrinterState(st);
      });
    };
    cek();
    /* ⚠️ Seirama dengan denyut server (5 dtk): dua lencana yang berbeda umur di satu baris
       membuat orang menyimpulkan yang satu rusak padahal ia cuma belum diperbarui. */
    const id = setInterval(cek, 5_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const status: HealthStatus = {
    server,
    printer,
    printerDetail: printerState,
    ...(queued === 0 ? {} : { queued }),
    ...(review === 0 ? {} : { needsReview: review }),
  };


  /** ⭐ Satu ketukan = satu item. Ketuk lagi ⇒ jumlah bertambah. */
  /**
   * Menandai / mencabut HABIS. ⭐ Layar diperbarui dari JAWABAN server, bukan ditebak duluan:
   * tanda yang muncul lalu diam-diam hilang saat poll berikutnya lebih membingungkan daripada
   * tanda yang datang setengah detik lebih lambat.
   */
  const ubahSoldOut = useCallback(async (code: string, next: boolean): Promise<void> => {
    try {
      await api.setSoldOut(outletCode, code, next);
      await muatSoldOut();
    } catch (e) {
      setError(pesanGalat(e));
    }
  }, [outletCode, muatSoldOut]);

  /**
   * ⭐⭐⭐ **Layar sedang KOSONG** — tak ada bill berjalan, tak ada baris, tak ada tagihan terhitung.
   *
   * Ini syarat yang sama yang dipakai K25 untuk membolehkan tarik otomatis saat sesi kas dibuka:
   * *"belum ada satu bill pun… tak ada tamu yang menunggu"*. Bedanya di sini keadaan itu berulang
   * puluhan kali sehari — tiap kali satu bill selesai.
   *
   * ⛔ Yang TIDAK dilakukan: menarik sendiri. Imron menolaknya dengan alasan yang benar — *"sync
   * harus diketahui orang outlet, jangan diam2"*. ⇒ yang muncul AJAKAN, dan kasir tetap yang
   * menekan.
   */
  const idle = line.length === 0 && orderId === null && bill === null;

  /**
   * ⭐⭐⭐ **Batas jualan luring TERLAMPAUI.**
   *
   * Diambil yang TERBESAR dari dua sumber, dan keduanya perlu:
   * - `hariLuring()` — perangkat ini tak menjangkau server (tier tablet);
   * - `outletOfflineDays` — mini-PC-nya sendiri tak menjangkau HQ (tier ber-server). ⛔ Tanpa yang
   *   kedua, batas ini tak pernah menyala di outlet ber-server: till selalu bisa menjangkau
   *   mini-PC, jadi angkanya selamanya nol walau outletnya sudah seminggu terputus.
   */
  /* ⚠️ `tikLuring` disebut supaya ketergantungan pada timer TERLIHAT — tanpa itu pembaca berikutnya
     akan mengira timernya sisa yang bisa dibuang. */
  void tikLuring;
  const hariTerputus = Math.max(hariLuring(), outletOfflineDays);
  const luringTerlaluLama = hariTerputus >= offlineMaxDays;

  /*
   * ⛔⛔ **Yang ditahan hanya bill BARU — bill yang sudah terbuka WAJIB bisa diselesaikan.**
   *
   * Prinsip yang sama dengan amandemen K17 dan K32: uang yang sudah berada di depan tamu tak boleh
   * dihentikan oleh keadaan sistem. Tamu yang makanannya sudah dimasak, lalu tak bisa membayar
   * karena tabletnya melewati hari ketiga, adalah kegagalan yang jauh lebih mahal daripada satu
   * penjualan tambahan yang menunggu rekonsiliasi.
   */
  const tahanBillBaru = luringTerlaluLama && idle;

  /* ⚠️ Diturunkan DI ATAS `add` dengan sengaja: `add` adalah `useCallback` yang menyebutnya di
     daftar dependensi, dan dependensi dibaca saat RENDER. Ditaruh di bawah, layar kasir blank
     tanpa satu pun galat — jebakan yang sudah memakan waktu sekali di repo ini (TDZ). */
  const add = useCallback((m: MenuItem) => {
    /*
     * ⛔ **Habis ⇒ berhenti DI SINI**, sebelum permintaan berangkat. Server tetap menolak
     * (`ORDER-409-SOLD_OUT`) — itu gerbang yang sesungguhnya; yang di sini cuma membuat penolakan
     * tak perlu terjadi sesudah kasir mengetiknya di depan tamu.
     */
    if (soldOutCodes.has(m.code)) {
      setError(t('cashier.soldOut.blocked', { name: m.name }));
      return;
    }
    /*
     * ⛔⛔⛔ **Batas jualan luring** (keputusan #4). Menahan bill BARU — bukan bill yang berjalan.
     *
     * ⚠️ `tahanBillBaru` sudah memuat syarat `idle`, jadi tamu yang bill-nya sudah terbuka tetap
     * bisa menambah pesanan dan membayar. Yang berhenti cuma memulai yang berikutnya.
     */
    if (tahanBillBaru) {
      setError(t('cashier.offlineLimit.blocked', { days: offlineMaxDays }));
      return;
    }
    /*
     * ⭐ **Menambah item sesudah bill dihitung itu NORMAL** — tamu memesan susulan sepanjang malam.
     *
     * Yang dulu dikunci di sini bukan masalah sesungguhnya: masalahnya adalah tiap hitung membuat
     * pesanan BARU sehingga yang lama menggantung memegang kunci meja. Itu sudah ditutup di
     * `pushLines` (satu pesanan, dipakai ulang) — jadi penguncian ini tinggal ongkos tanpa guna.
     *
     * ⚠️ Bill lama dibuang begitu isinya berubah: total basi lebih berbahaya daripada tak ada
     * total, karena kasir bisa menagih angka yang sudah tak benar.
     */
    /*
     * ⭐⭐ **Set menu membuka STEPPER di container ini**, bukan dialog melayang (Imron, 2026-08-07:
     * *"kamu pakai box container menu dan meja saja, draw disitu, ada tombol next dan back"*).
     *
     * ⛔ Diperiksa dari `productKind`, BUKAN dari "ada grupnya atau tidak". Set menu yang grupnya
     * belum tersusun tetap combo — dan ia harus berhenti di sini dengan kalimat yang menyebut
     * masternya belum lengkap, bukan diam-diam jatuh ke jalur biasa dan lahir sebagai baris polos
     * seharga paket tanpa satu pun komponen (KB16).
     *
     * ⭐⭐⭐ **Tier satu-tablet AKHIRNYA BISA** (Imron, 2026-08-25: *"di mode quick service item set
     * menu kenapa tidak bisa?"*). Dulu ia ditolak karena amplop `/sync/sale` tak punya bentuk untuk
     * baris anak — paketnya mendarat tanpa isi, dapur tak tahu, backflush menghitung nol (K30).
     * ⇒ `SaleLineInput.comboSelections` menutup lubang itu; `sale-ingest` memanggil `addComboLine`.
     *
     * ⚠️ Aritmetikanya TIDAK berubah, dan itu yang membuatnya aman: `bill()` menagih INDUKNYA saja
     * (komponen di-`filter` keluar), jadi satu baris set menu berharga paket — persis yang sudah
     * dihitung `assembleSale`. Yang wajib benar: harganya sudah termasuk upcharge (`ComboPicker`
     * sudah mengirimnya begitu), kalau tidak tiap penjualan ditolak `SYNC-409-VARIANCE`.
     */
    if ((m.productKind ?? 'simple') === 'combo') {
      setComboFor(m);
      setView('combo');
      return;
    }
    /*
     * ⭐ Produk bermodifier BERTANYA dulu. Prinsip "satu ketukan = satu item" tetap berlaku untuk
     * produk polos; yang bermodifier punya pertanyaan yang harus dijawab sebelum barangnya bisa
     * dimasak, dan menyimpannya untuk ditanya belakangan berarti tiket dapur berangkat tanpa
     * jawaban.
     */
    if (modifierGroups.some((g) => g.productCode === m.code)) {
      setModFor(m);
      return;
    }
    setBill(null); // bill lama tak berlaku begitu isi berubah
    tambahBaris(m, [], []);
  }, [modifierGroups, orderId, tablet, t, soldOutCodes, tahanBillBaru, offlineMaxDays]);

  /**
   * ⚠️ Baris bermodifier TIDAK digabung dengan baris lain berkode sama: "pedas" dan "tak pedas"
   * adalah dua masakan berbeda. Yang digabung hanya baris polos dengan baris polos.
   */
  const tambahBaris = useCallback(
    (
      m: MenuItem,
      modifiers: readonly ModifierSelectionInput[],
      labels: readonly string[],
      combo?: readonly ComboSelectionInput[],
    ) => {
      setBill(null);
      /* ⚠️ Dibuat DI LUAR updater: React StrictMode menjalankan updater dua kali di dev, dan kunci
         yang lahir di dalamnya jadi berbeda antara jalan pertama & kedua. */
      lineSeq += 1;
      const key = `${m.code}#${String(lineSeq)}`;
      setLines((b) => {
        /* ⛔ Baris ber-combo tak ikut penggabungan — dua paket bersusunan beda bukan satu baris
           berjumlah dua. Alasan yang sama dengan modifier. */
        if (modifiers.length === 0 && combo === undefined) {
          const ada = b.find((x) =>
            x.code === m.code && x.modifiers.length === 0 && x.combo === undefined);
          if (ada !== undefined) {
            return b.map((x) =>
              x.code === m.code && x.modifiers.length === 0 && x.combo === undefined
                ? { ...x, qty: x.qty + 1 }
                : x);
          }
        }
        return [
          ...b,
          {
            key, code: m.code, name: m.name, price: m.price, qty: 1, sentQty: 0, serverQty: 0,
            modifiers, modifierLabels: labels,
            ...(combo === undefined ? {} : { combo }),
          },
        ];
      });
    },
    [],
  );

  /*
   * ⛔ Berhenti di 1 — TIDAK menghapus baris.
   *
   * Sebelumnya `−` pada jumlah 1 membuang barisnya. Artinya satu ketukan kelebihan menghapus baris
   * tanpa peringatan, dan di jam sibuk ketukan kelebihan adalah keadaan normal. Menghapus kini
   * keputusan tersendiri (`removeLine`), bukan akibat sampingan dari mengurangi jumlah.
   */
  /* ⚠️ Sasarannya `key`, bukan `code` — lihat alasannya di `Line.key`. */
  const decrement = useCallback((key: string) => {
    setBill(null);
    /*
     * ⛔ Tak boleh turun di bawah yang SUDAH TERKIRIM. Yang sudah di dapur tak bisa ditarik pulang
     * dengan menekan tombol; membatalkannya adalah peristiwa ber-alasan, bukan penyuntingan layar.
     */
    setLines((b) =>
      b.map((x) => (x.key === key && x.qty > Math.max(1, x.sentQty) ? { ...x, qty: x.qty - 1 } : x)),
    );
  }, []);

  const increment = useCallback((key: string) => {
    setBill(null);
    setLines((b) => b.map((x) => (x.key === key ? { ...x, qty: x.qty + 1 } : x)));
  }, []);

  /**
   * ⛔⛔ Menghapus baris **juga di server** bila ia sudah tersimpan di sana.
   *
   * Sejak pesanan bisa DITAHAN, barisnya hidup di server sebelum ditagih. Membuangnya dari layar
   * saja berarti item itu tetap ikut tertagih — dan tak satu pun angka terlihat salah; yang salah
   * cuma jumlah yang dibayar tamu.
   *
   * ⚠️ Layar menunggu jawaban server SEBELUM membuang barisnya. Membuang lebih dulu membuat kasir
   * mengira item itu hilang padahal permintaannya gagal.
   */
  const removeLine = useCallback((key: string) => {
    const target = line.find((x) => x.key === key);
    // ⛔ Baris yang sebagian sudah terkirim tak bisa dihapus dari layar — lihat `decrement`.
    if (target === undefined || target.sentQty > 0) return;

    const buang = () => {
      setBill(null);
      setLines((b) => b.filter((x) => x.key !== key));
      setSelected((cur) => (cur === key ? null : cur));
    };

    if (orderId === null || target.serverLineId === undefined) { buang(); return; }
    void api.removeLine(orderId, target.serverLineId)
      .then(buang)
      .catch((e: Error) => { setError(pesanGalat(e)); });
  }, [line, orderId]);

  /**
   * Subtotal PERKIRAAN — dipakai hanya sampai server menjawab dengan bill sungguhan.
   *
   * ⛔⛔ **Tambahan modifier WAJIB ikut** (2026-08-26). Tanpa itu panel bill tak berjumlah:
   * Es Kopi + Extra Shot tampil "Subtotal 25.000 · SC 1.650 · PB1 3.465 · Total 38.115" — empat
   * angka uang yang tak bisa dijumlahkan satu sama lain. Kasir yang mengecek ulang di depan tamu
   * menemukan layar yang membantah dirinya sendiri, dan yang paling mungkin ia percayai justru
   * angka yang paling kecil.
   */
  const subtotalKasar = line.reduce(
    (s, x) =>
      s
      + (Number(x.price.split('.')[0] ?? '0')
        + Number(deltaModifier(x).split('.')[0] ?? '0')) * x.qty,
    0,
  );

  /**
   * ⭐⭐⭐ **Gerbangnya di SERVER; layar yang membukakan jalan keluarnya.**
   *
   * ### ⛔ Kenapa BUKAN dialog yang memotong tiap kali
   * Percobaan pertama memasang dialog wajib sebelum tiap Kirim/Bayar. Ia benar secara aturan dan
   * salah secara alur: **52 dari 165 uji kasir langsung merah**, dan angka itu bukan soal uji —
   * ia mengukur berapa banyak alur nyata yang mendadak butuh satu ketukan tambahan **sebelum**
   * kasir tahu apakah ia memang dibutuhkan (pesanan rokok tak pernah sampai ke dapur).
   *
   * ⇒ Yang MEMAKSA tetap server (`ORDER-409-NO_TAG`, di titik interaktif — tak bisa dilewati
   *   DevTools). Yang dikerjakan layar: menyebut penandanya terus-menerus di atas bill, dan
   *   **membuka dialognya sendiri** begitu server menolak — jadi penolakan itu berujung pada kotak
   *   isian yang siap diketik, bukan pada pesan galat yang harus dicerna di depan tamu.
   */
  /**
   * ⭐ Kotak mana yang disorot saat dialog penanda terbuka.
   *
   * ⛔⛔ Lewat `useCallback` yang IDENTITASNYA stabil, bukan arrow inline. Arrow baru tiap render
   * membuat React melepas lalu memasang ulang `ref` — jadi `select()` berjalan LAGI setiap huruf
   * diketik, dan tiap huruf berikutnya menimpa yang sebelumnya. Mengetik "Rina" menghasilkan "a".
   * Ketahuan dari TW7, bukan dari membaca kode.
   */
  const fokusTanda = tahanTanda?.focus ?? 'label';
  const refLabelTanda = useCallback((el: HTMLInputElement | null) => {
    if (el !== null && fokusTanda !== 'pax') { el.focus(); el.select(); }
  }, [fokusTanda]);
  const refPaxTanda = useCallback((el: HTMLInputElement | null) => {
    if (el !== null && fokusTanda === 'pax') { el.focus(); el.select(); }
  }, [fokusTanda]);

  const bukaDialogTanda = useCallback((
    lanjut?: () => void,
    /** Kotak yang langsung siap diketik. Bawaan: label — jalur yang paling sering dipakai. */
    focus: 'label' | 'pax' = 'label',
  ): void => {
    lanjutSesudahTanda.current = lanjut ?? null;
    const kind = table !== null ? 'table' : 'name';
    /*
     * ⭐⭐⭐ **Nama tamu bawaan `guest_01`, `guest_02`, …** (Imron, 2026-08-25: *"ini untuk
     * kecepatan kasir saja tanpa perlu repot ketik nama"*).
     *
     * ⛔ HANYA di quick service, dan hanya bila belum ada penanda: di layanan meja penandanya
     * MEJA, dan mengisinya dengan nama karangan berarti dapur menerima "guest_03" untuk tamu yang
     * duduk di meja 7 — persis kebalikan dari yang K31 selamatkan.
     * ⚠️ Ia terisi sebagai TEKS BIASA yang bisa ditimpa, bukan nilai terkunci. Kasir yang tahu
     * nama tamunya tetap mengetik namanya, dan tak ada nomor yang terbakar untuk itu.
     */
    const bawaan = saleMode === 'quick_service' && kind === 'name' && tanda === null
      ? peekGuestName(props.businessDate ?? '')
      : '';
    setTahanTanda({
      mode: 'tag',
      kind,
      text: tanda?.label ?? bawaan,
      guests: String(tanda?.guests ?? table?.guests ?? 1),
      focus,
    });
  }, [table, tanda, saleMode, props.businessDate]);

  /**
   * ⭐⭐⭐ **Satu-satunya tempat galat server diterjemahkan jadi layar** — dan karena itu
   * satu-satunya tempat `ORDER-409-IN_USE` punya kesempatan jadi jalan keluar, bukan buntu.
   *
   * ⛔ Dulu tiap aksi menyalin tiga baris yang sama (`detail` → `error_code · trace_id`). Selama
   * begitu, bill yang terkunci di perangkat mati muncul sebagai kode galat mentah di sudut layar:
   * kasir membacanya sebagai "rusak", lalu menelepon. Jalan keluarnya SUDAH ADA di server sejak
   * lama — yang tak ada cuma pintunya.
   *
   * ⚠️ `retry` bukan hiasan: yang diinginkan kasir bukan "kunci berpindah", melainkan bill itu
   * terbuka. Sesudah paksa-buka berhasil, aksi yang tadi ditolak dijalankan ulang sendiri.
   */
  const handleError = useCallback((e: unknown, retry: () => void | Promise<void>) => {
    const d = (e as { detail?: ApiError }).detail;
    if (d?.error_code === 'ORDER-409-IN_USE') {
      const c = d.context ?? {};
      const name = c['heldByName'];
      const secs = c['heldSeconds'];
      setHoldConflict({
        orderId: String(c['orderId'] ?? ''),
        heldByName: typeof name === 'string' ? name : null,
        heldSeconds: typeof secs === 'number' ? secs : null,
        retry,
      });
      return;
    }
    /*
     * ⭐⭐⭐ **Penolakan penanda berujung pada KOTAK ISIAN, bukan kode galat.**
     *
     * Server menolak kirim-ke-dapur tanpa penanda (`ORDER-409-NO_TAG` / `NO_GUESTS`) — dan itu
     * memang kontrolnya, di titik yang tak bisa dilewati DevTools. Tapi kasir yang berdiri di depan
     * tamu tak bisa berbuat apa-apa dengan kode galat; yang ia butuhkan kotak isian yang sudah
     * terbuka. ⚠️ `retry` dijalankan sendiri sesudah penandanya tersimpan, jadi tak ada pekerjaan
     * yang terulang.
     */
    if (d?.error_code === 'ORDER-409-NO_TAG' || d?.error_code === 'ORDER-409-NO_GUESTS') {
      bukaDialogTanda(() => { void retry(); });
      return;
    }
    /*
     * ⭐⭐⭐ **Nomor kembar diperbaiki SENDIRI, tanpa kasir tahu apa-apa.**
     *
     * Keunikan nomor bill itu struktural (prefiks `device_code` + hari + urut lokal), jadi tabrakan
     * cuma punya satu sebab nyata: **ingatan perangkat hilang di tengah hari** — data situs
     * dibersihkan, atau tabletnya dipasang ulang. Sesudah itu penghitung kembali ke satu sementara
     * nomor 1..120 sudah dicetak.
     *
     * ⛔ Menampilkan kode galat di sini berarti kasir menabrak nomor kembar seratus kali berturut,
     * dan tiap tabrakan adalah satu pesanan yang gagal dibuka di depan tamu. ⇒ server mengirim
     * `nextSeq` (urut BEBAS berikutnya, dihitung dari nomor yang benar-benar ada), layar
     * menerimanya, dan mengulang aksinya. Sekali lompat, bukan seratus langkah.
     */
    if (d?.error_code === 'ORDER-409-DUP_NO') {
      const n = (d.context ?? {})['nextSeq'];
      if (typeof n === 'number') {
        adoptNextSeq(deviceCode, props.businessDate ?? '', n);
        nomorBill.current = null;
        void retry();
        return;
      }
    }
    // Kode error + trace_id ditampilkan apa adanya: itulah yang dipakai operator untuk grep.
    setError(d === undefined ? String(e) : `${d.error_code} · ${d.trace_id}`);
  }, [bukaDialogTanda, deviceCode, props.businessDate]);

  /**
   * ⭐ **Satu pesanan, didorong sedikit demi sedikit.**
   *
   * ⛔ Yang didorong ke server hanya SELISIHNYA (`qty - serverQty`) — bukan seluruh isi layar.
   * Mendorong ulang semuanya berarti dapur menerima pesanan yang sama dua kali, dan bill
   * memuat dua baris untuk makanan yang cuma satu.
   *
   * ⚠️ Pembandingnya `serverQty` (**sudah tersimpan**), ⛔ bukan `sentQty` (**sudah ke dapur**).
   * Lihat `Line.serverQty`: menyamakan keduanya membuat pesanan yang dipanggil kembali didorong
   * ulang seluruhnya.
   *
   * ⚠️ Pesanannya dibuat SEKALI lalu dipakai ulang. Membuat pesanan baru tiap kali menghitung
   * meninggalkan pesanan lama menggantung di server sambil memegang kunci meja — tanpa galat,
   * tanpa gejala, dan mejanya tak bisa dipakai sampai ada yang membukanya paksa.
   */
  const pushLines = useCallback(async (
    /**
     * ⭐ Menyusulkan penanda ke server sesudah pesanannya lahir. **Bawaannya ya** — itu satu-satunya
     * jalan penanda yang diketik SEBELUM bill ada sampai ke dapur (G-3).
     *
     * ⛔ Dimatikan hanya oleh `tahan()`, yang mengirim penandanya sendiri lewat `park` sedetik
     * kemudian. Membiarkannya menyala di situ bukan cuma boros satu panggilan: ia menaruh titik
     * gagal BARU di depan penahanan, jadi penanda yang bermasalah membuat billnya gagal ditahan —
     * padahal `park` membawa penanda yang sama.
     */
    tagIkut = true,
  ): Promise<string> => {
    const actor = pelaku();
    /* ⛔ Dari REF, bukan dari state: dalam satu ketukan Bayar fungsi ini dipanggil dua kali, dan
       state yang belum ter-render membuat panggilan kedua membuka pesanan KEDUA. */
    let id = orderIdRef.current;
    if (id === null) {
      const order = await api.openOrder({
        outletCode,
        /*
         * ⭐⭐⭐ **Nomor dari IDENTITAS perangkat, bukan dari jam** (§4b `design/OFFLINE-FIRST.md`).
         * Yang lama, `UI/${Date.now().slice(-6)}/1`, berulang tiap ±16,7 menit sementara
         * `order_no_uniq` berlaku selamanya ⇒ tabrakan menumpuk sampai satu pesanan gagal dibuka
         * di depan tamu. Dipanggil di sini karena di sini persis satu kali per pesanan (`id === null`).
         */
        orderNo: takeOrderNo(deviceCode, props.businessDate ?? ''),
        /* ⛔ Tanggal usaha SESI KAS — bukan jam tablet. Alasan lengkap di `App.tsx`. */
        businessDate: props.businessDate ?? '',
        orderType: 'dine_in',
        entryChannel: 'pos',
        priceChannel,
        /*
         * ⭐⭐⭐ **Meja & jumlah tamu ikut saat pesanan LAHIR** (G-1, ditemukan UAT 2026-08-26).
         *
         * ⛔ Sebelum ini keduanya berhenti di keadaan LAYAR: panel bill menulis "MEJA T-01 · 2 org"
         * sementara pesanan di server lahir tanpa meja dan ber-`guest_count` 1. Akibatnya penanda
         * `meja` ditolak server, kirim-ke-dapur buntu, dan `Hitung Bill` tetap mati ⇒ resto bermeja
         * tak bisa berjualan sama sekali.
         *
         * ⚠️ `guests` bisa 0 saat pesanan dipanggil kembali dari daftar (lihat `setTable` di
         * `muatPesanan`) — jatuh ke 1, bukan 0: penyebut *average per cover* tak boleh nol.
         */
        ...(table === null ? {} : { tableCode: table.code }),
        guestCount: table === null || table.guests <= 0 ? 1 : table.guests,
        deviceCode,
        actor,
      });
      id = order.id;
      setOrderId(id);
      props.onOrderChanged?.(id);
    }
    for (const b of line) {
      const delta = b.qty - b.serverQty;
      if (delta <= 0) continue;
      /*
       * ⭐⭐ **Set menu lewat endpoint SENDIRI** — dan itulah yang menerbitkan baris komponennya.
       *
       * ⛔ Sejak KB16 server MENOLAK combo di `/lines`, jadi salah jalur di sini bukan lagi
       * kesalahan senyap: ia gagal keras, di layar, sebelum bill lahir. Sebelum itu, paketnya
       * masuk sebagai satu baris seharga paket dan dapur tak pernah tahu harus memasak apa.
       */
      if (b.combo !== undefined) {
        await api.addComboLine(id, {
          productCode: b.code,
          qty: `${String(delta)}.000000`,
          selections: b.combo,
          actor,
        });
        continue;
      }
      await api.addLine(id, {
        productCode: b.code,
        qty: `${String(delta)}.000000`,
        /* ⛔ Modifier ikut ke SERVER: server yang menegakkan min/max & harga tambahannya. */
        ...(b.modifiers.length === 0 ? {} : { modifiers: b.modifiers }),
        actor,
      });
    }
    /*
     * ⛔ Ditandai SESUDAH semua permintaan lolos. Menandainya lebih dulu berarti baris yang gagal
     * terkirim dianggap sudah tersimpan — lalu ia hilang dari bill tanpa satu pun galat.
     */
    const didorong = new Map(line.map((b) => [b.key, b.qty] as const));
    setLines((bs) =>
      bs.map((x) => ({ ...x, serverQty: Math.max(x.serverQty, didorong.get(x.key) ?? 0) })));

    /*
     * ⭐⭐⭐ **Penanda menyusul ke server di sini** — dan tanpa baris ini seluruh fiturnya bocor.
     *
     * Kasir mengetik nama SEBELUM bill lahir di server (itu urutan yang paling alami: item ditekan,
     * nama diketik, baru dikirim). Saat itu `orderId` masih `null`, jadi `simpanTanda` tak punya ke
     * mana mengirimnya dan menyimpannya di layar saja. ⛔ Kalau berhenti di situ, penandanya tak
     * pernah sampai ke dapur — persis keadaan yang melahirkan keluhan ini.
     *
     * ⚠️ Kegagalannya TIDAK ditelan: kalau penanda gagal tersimpan, kirim-ke-dapur berikutnya akan
     * ditolak server (`ORDER-409-NO_TAG`) dan dialognya terbuka sendiri — jalan keluarnya sama.
     */
    /* ⛔ Dari REF, bukan dari state — lihat `tandaRef`. Nilai state di sini masih milik render
       sebelum penandanya diisi, dan itulah yang dulu membuat penanda berhenti di layar (G-3). */
    const t = tandaRef.current;
    if (tagIkut && t !== null && t.kind !== 'table') {
      await api.tagOrder(id, {
        kind: t.kind, label: t.label, guestCount: t.guests,
      });
    }
    return id;
  }, [line, outletCode, deviceCode, priceChannel, props, setOrderId]);

  /**
   * ⭐⭐ **KIRIM KE DAPUR** — di outlet ber-`kitchen_routing`/`table_service` inilah aksi utamanya,
   * bukan "Hitung Bill".
   *
   * Urutan kerja nyatanya: pelayan mencatat → **kirim** → tamu makan → bill → bayar. Layar yang
   * menawarkan "Hitung Bill" sebagai satu-satunya tombol memaksa pelayan menagih sebelum
   * makanannya dimasak.
   *
   * ⛔ Sesudah terkirim, baris masuk **rezim kontrol yang berbeda**: yang sudah di dapur tak bisa
   * dikurangi dengan menekan tombol (§10).
   */
  /**
   * Mengantar tiket dapur ke printernya. ⛔ **Tak pernah melempar** — lihat `sendOrder`.
   *
   * ⚠️ Tiap tiket punya ALAMATNYA SENDIRI: struk keluar di printer kasir, tiket di printer dapur,
   * dan dua-duanya lewat agen yang sama di tablet ini. Itu bisa karena alamat ikut di tiap job,
   * bukan disetel di agen.
   */
  /**
   * ⭐⭐⭐ **Mencetak tiket dapur yang dirakit DI LAYAR** (tier tablet, luring).
   *
   * ⛔ Byte tetap dirender kode BERSAMA (`@osbpos/domain`), bukan dikarang di sini — aturan yang
   * sama dengan yang dipakai server. K27 melarang klien merakit byte STRUK karena tendangan laci
   * ikut di dalamnya; tiket dapur tak dekat dengan uang dan tak punya `ESC p`, jadi merendernya di
   * layar tak membuka pintu apa pun. ⚠️ Yang tetap tak boleh: mengarang FORMATnya sendiri — dapur
   * harus melihat kertas yang sama, dari mana pun ia dicetak.
   */
  const cetakTiketLokal = useCallback(
    async (tk: { station: string; printer: unknown; doc: KitchenTicketDoc }): Promise<void> => {
      try {
        const profile = tk.printer as PrinterProfile | null;
        if (profile === null || profile === undefined) return;
        const bytes = renderKitchenTicket(tk.doc, profile);
        let biner = '';
        for (const b of bytes) biner += String.fromCharCode(b);
        await printViaAgent({ bytesBase64: btoa(biner), address: profile.address });
      } catch {
        /* ⛔ Sengaja bisu — lihat catatan di `cetakTiketDapur`: kertas yang tak keluar bukan
           alasan menahan tamu di depan kasir. */
      }
    },
    [],
  );

  const cetakTiketDapur = useCallback(async (id: string, round: number): Promise<void> => {
    try {
      const r = await api.kitchenTicketJobs(id, round);
      for (const job of r.jobs) {
        await printViaAgent({ bytesBase64: job.bytesBase64, address: job.address });
      }
    } catch {
      /* ⛔ Sengaja bisu: pesanannya sudah sampai server, dan kertas yang tak keluar bukan alasan
         menahan tamu di depan kasir. Lencana printer yang mengabarkan keadaannya. */
    }
  }, []);

  /**
   * ⭐⭐⭐ **Struk keluar SENDIRI begitu penjualan lunas** (Imron, 2026-08-25: *"waktu saya tekan
   * tombol pay setelah pilih cash, harusnya kalau sukses tender langsung command printing bill"*).
   *
   * ### ⛔ Catatan lama di layar ini berbunyi kebalikannya
   * *"Otomatis terdengar ramah dan salah: tak semua tamu minta struk, kertas termal itu biaya
   * nyata"*. Itu penalaran saya, bukan penalaran orang yang berdiri di kasir. Di F&B Indonesia
   * struk keluar setiap transaksi, dan yang benar-benar terjadi tanpa otomatis adalah kasir
   * menekan tombol kedua di depan tamu yang sudah mengulurkan tangan.
   *
   * ### ⚠️ Gerbang printer DILEPAS (Imron, 2026-08-25: *"langsung saja, regardless ada printer
   * atau tidak"*)
   * Sebelumnya ia hanya jalan bila `printerState === 'ready'`, demi menahan `printCount` naik untuk
   * kertas yang tak pernah ada (Guardrail #8). Keberatan itu saya sampaikan dan Imron memutuskan
   * sebaliknya — dan alasannya kuat: lencana printer menjawab keadaan AGEN, bukan keadaan printer,
   * jadi ia bisa merah pada terminal yang sebenarnya bisa mencetak (mis. jalur `net` dari
   * outlet-server). Gerbang yang salah menahan berarti struk yang tak pernah keluar tanpa sebab
   * yang bisa dilihat siapa pun.
   *
   * ⇒ Yang menggantikannya: layar WAJIB membedakan tiga keadaan, dan "tercatat tapi kertas belum
   * keluar" berbunyi begitu — bukan "gagal". Itu yang membuat `printCount` yang kelebihan tetap
   * bisa dijelaskan.
   *
   * ⚠️ Byte tetap dirender SERVER (K27) — tendangan laci ada di dalamnya, jadi klien tak pernah
   * merakit struk sendiri. Di tier tablet ini berarti struk keluar sesudah dokumennya MENDARAT.
   */
  /**
   * ⭐ Profil printer perangkat INI — dari `GET /devices/me`, disalin supaya tetap ada saat luring.
   */
  const profilPrinter = (props.printer ?? null) as PrinterProfile | null;

  /**
   * ⭐⭐ Apakah laci sudah BENAR-BENAR tertendang untuk penjualan ini.
   *
   * ⛔ Bukan "sudah dicoba". Kalau tendangan klien gagal (agen mati) dan struknya tetap dikirim
   * dengan `drawerKicked: true`, lacinya tak terbuka sama sekali — dan kasir yang memegang uang
   * kembalian tak punya jalan lain selain kunci fisik.
   */
  const laciTertendang = useRef(false);

  /**
   * ⭐⭐⭐ **Penutupan otomatis** (Imron, 2026-08-25: *"kenapa harus tekan 'tutup' dulu baru
   * printing? langsung saja"*).
   *
   * ⚠️ Lewat `ref`, bukan pemanggilan langsung: `close` didefinisikan JAUH di bawah `pay`, dan
   * menyebutnya di daftar dependensi `pay` berarti membacanya sebelum ia ada (TDZ) — layar kasir
   * tak menyala sama sekali, di setiap render.
   */
  const tutupRef = useRef<((lunas?: AssembledSale) => void) | null>(null);

  /**
   * ⭐⭐⭐ **Tendang laci di titik TENDER, bukan menunggu struk** (Imron, 2026-08-25: *"tendang
   * laci tidak perlu lewat server. waktu pilih tender bertipe tendang laci, kasir harusnya sudah
   * tahu"*).
   *
   * ### ⛔⛔ Apa yang berubah dari K27, dan apa yang TIDAK
   * K27 melarang klien merakit **struk**, dan itu tetap berlaku: struk memuat angka uang yang wajib
   * identik dengan yang tercatat. Tendangan laci tak memuat satu pun angka — dan `opens_drawer`
   * sudah ikut turun di katalog sejak lama, jadi kasir memang **sudah tahu**. Menunggu server
   * mengembalikan byte hanya untuk memberitahukan apa yang sudah ada di tangannya berarti laci tak
   * bisa dibuka saat jaringan mati, persis ketika kembalian paling dibutuhkan.
   *
   * ⚠️ Yang DILEPAS, dan disadari: dulu laci hanya bisa terbuka bersama struk pesanan yang sudah
   * LUNAS di server. Sekarang ia terbuka saat tender diterima di layar — termasuk di tier tablet,
   * di mana belum ada apa pun yang sampai ke server. Yang menjaga tetap ada di tempat lain: buka
   * laci TANPA penjualan tetap menuntut `drawer:open` + PIN, dan membatalkan bill sesudah tender
   * tetap berjejak.
   *
   * ⛔ Byte-nya dari `@osbpos/domain` (`drawerKickBytes`), tak pernah dikarang di shell — sumber
   * yang sama dengan yang dipakai renderer struk.
   */
  const tendangLaci = useCallback(async (m: PaymentMethod): Promise<void> => {
    if (m.opensDrawer !== true || profilPrinter === null) return;
    const bytes = drawerKickBytes(profilPrinter);
    if (bytes.length === 0) return;
    let biner = '';
    for (const b of bytes) biner += String.fromCharCode(b);
    const kirim = await printViaAgent({
      bytesBase64: btoa(biner),
      address: profilPrinter.address,
    });
    /* ⚠️ Hanya yang BERHASIL yang menahan tendangan kedua di struk. */
    if (kirim.ok) laciTertendang.current = true;
  }, [profilPrinter]);

  const cetakStruk = useCallback(async (id: string): Promise<void> => {
    setAutoPrint({ kind: 'busy' });
    const st = staffSession();
    try {
      const hasil = await api.print(id, {
        docType: 'receipt',
        deviceCode,
        /* ⛔ Menahan tendangan KEDUA. Tanpa ini laci membuka sendiri saat struk keluar — dan di
           tier tablet jeda dari tender ke struk bisa semenit, jadi laci yang sudah ditutup
           terbuka lagi tanpa ada yang menyentuhnya. */
        ...(laciTertendang.current ? { drawerKicked: true } : {}),
        actor: { actorKind: 'staff', code: st?.staffCode ?? '', name: st?.displayName ?? '' },
      });
      /* ⛔ Sampai sini pencetakan SUDAH tercatat di server — apa pun yang gagal sesudahnya bukan
         lagi "gagal mencetak". Kalimatnya wajib membedakannya, sama seperti `PrintButton`. */
      if (hasil.job === undefined) {
        setAutoPrint({ kind: 'warn', message: hasil.printError?.message ?? t('cashier.print.noJob') });
        return;
      }
      const kirim = await printViaAgent({
        bytesBase64: hasil.job.bytesBase64,
        address: hasil.job.address,
      });
      setAutoPrint(kirim.ok
        ? { kind: 'ok' }
        : { kind: 'warn', message: `${kirim.code ?? ''} ${kirim.message ?? ''}`.trim() });
    } catch (e) {
      /* ⚠️ Tier tablet: dokumennya mungkin belum mendarat (luring) ⇒ belum ada yang bisa dirender.
         Itu keadaan yang SAH dan harus dikatakan, bukan galat yang menakut-nakuti kasir. */
      setAutoPrint({ kind: 'warn', message: (e as Error).message });
    }
  }, [deviceCode, t]);

  /**
   * ⚠️ Mengembalikan **ID pesanan**, bukan `void` dan bukan lagi boolean.
   *
   * Quick service merangkai kirim→bill dalam satu ketukan; rangkaian yang tak tahu langkah
   * pertamanya gagal akan menagih makanan yang tak pernah sampai ke dapur. ⭐⭐ Dan yang lebih
   * halus: langkah kedua harus tahu pesanan MANA yang baru saja dipakai. Menyuruhnya mencari
   * sendiri lewat state berarti ia membaca nilai render sebelumnya — persis sebab dua-pesanan
   * 2026-08-23. `null` = gagal.
   */
  const sendOrder = useCallback(async (): Promise<string | null> => {
    if (line.every((b) => b.qty - b.sentQty <= 0)) return orderIdRef.current;
    setBusy(true);
    setError(null);
    try {
      /*
       * ⭐⭐⭐ **Tier tablet: tiketnya dirakit & dicetak DI SINI.**
       *
       * ⛔ Sampai 2026-08-23 cabang ini tak ada, dan akibatnya warung BERDAPUR tak pernah bisa
       * tier tablet sama sekali: tombol Bayar menuntut baris berangkat ke dapur, `sendToKitchen`
       * lewat server, jadi seluruh outlet berhenti jualan tiap internet putus — padahal justru
       * merekalah yang paling sering putus.
       *
       * ⚠️ Yang dirakit di sini cuma KERTASNYA. Baris `kitchen_ticket` tetap lahir di server saat
       * dokumen penjualannya menyusul (`sale-ingest` memanggil `sendToKitchen`) — jadi KDS &
       * laporan tetap melihatnya, dan tak ada dua sumber kebenaran soal apa yang pernah dimasak.
       */
      if (tablet) {
        const belum = line.filter((b) => b.qty - b.sentQty > 0);
        const { tickets, unrouted } = assembleLocalKitchenTickets({
          lines: belum.map((b) => ({
            code: b.code, name: b.name, qty: b.qty - b.sentQty,
            modifierLabels: b.modifierLabels,
          })),
          stations: kitchen?.stations ?? [],
          routes: kitchen?.routes ?? [],
          label: table?.code ?? tandaTersimpan?.label ?? '-',
          /*
           * ⭐⭐ Nomor yang SAMA dengan dokumen penjualannya. Sebelum ini tiketnya dicetak
           * `T/<jam>` sementara dokumennya lahir `T/<potongan uuid>` ⇒ satu penjualan punya dua
           * nomor yang tak pernah cocok di laporan mana pun, dan tak ada gejalanya di layar.
           */
          orderNo: (nomorBill.current ??= takeOrderNo(deviceCode, props.businessDate ?? '')),
          sendRound: 1,
          printedAt: new Date().toISOString(),
        });
        /*
         * ⭐⭐⭐ **Yang belum dirutekan DILAPORKAN, tak memblokir** (Imron, 2026-08-23):
         * *"ada atau tidaknya printer/KDS di kitchen tidak boleh menghentikan penjualan."*
         *
         * ⛔ Sempat menolak pengiriman di sini, dan itu salah dengan cara yang sama seperti di
         * server: kelalaian data master (menu baru lupa dirutekan) menghentikan PENJUALAN, di
         * depan tamu. Yang lupa merutekan orang di kantor; yang menanggung penolakannya kasir.
         *
         * ⚠️ K17 tetap berdiri — ia tak pernah DIAM: pemberitahuan tetap muncul, dan portal punya
         * peringatannya sendiri. Yang berhenti cuma menolak uangnya.
         */
        if (unrouted.length > 0) {
          setKitchenNotice(t('cashier.kitchen.unrouted', { codes: unrouted.join(', ') }));
        }
        for (const tk of tickets) {
          /* ⛔ Gagal cetak TIDAK menghalangi transaksi (Imron, 2026-08-22) — lencana printer yang
             mengabarkan keadaannya, dan tiketnya bisa dicetak ulang dari server nanti. */
          void cetakTiketLokal(tk);
        }
        setLines((b) => b.map((x) => ({ ...x, sentQty: x.qty })));
        setBill(null);
        return orderIdRef.current ?? 'tablet';
      }

      const id = await pushLines();
      const kirim = await api.sendOrder(id, { actor: pelaku() });
      /* ⭐ Server kini MELAPORKAN yang tak punya tujuan alih-alih menolak (2026-08-23). Layar
         mengatakannya sebagai pemberitahuan, bukan galat — penjualan tetap berjalan. */
      if ((kirim.unrouted ?? []).length > 0) {
        setKitchenNotice(t('cashier.kitchen.unrouted', { codes: (kirim.unrouted ?? []).join(', ') }));
      }
      // Yang tadinya "baru" kini "terkirim" — tandanya muncul di baris, bukan di catatan kasir.
      setLines((b) => b.map((x) => ({ ...x, sentQty: x.qty })));
      setBill(null);
      /*
       * ⭐⭐ **Tiket dapur dicetak SESUDAH server menerima kirimannya** — dan sengaja TIDAK
       * di-`await` sebagai bagian dari keberhasilan (Imron, 2026-08-22: *"gagal cetak jangan
       * menghalangi transaksi, biarkan saja"*).
       *
       * ⛔ Urutannya penting: tiketnya sudah tercatat di server sebelum satu byte pun dikirim
       * (K27 — catat dulu, baru render). Jadi printer yang mati berarti kertas yang tak keluar,
       * ⛔ bukan pesanan yang hilang: KDS & laporan tetap melihatnya, dan tiketnya bisa dicetak
       * ulang.
       *
       * ⚠️ Galatnya ditelan DENGAN SENGAJA di sini, dan itu aman justru karena lencana printer di
       * pita atas sudah mengatakan keadaan jalur cetak terus-menerus. Kalau lencana itu tak ada,
       * menelan galat di sini akan jadi kegagalan senyap.
       */
      void cetakTiketDapur(id, kirim.sendRound);
      return id;
    } catch (e) {
      handleError(e, () => void sendOrder());
      return null;
    } finally {
      setBusy(false);
    }
  }, [line, pushLines, handleError, tablet, kitchen, table, tandaTersimpan, t]);

  /**
   * ⭐ **Bill dihitung SERVER, bukan di sini.** Angka di layar sebelum ini hanyalah perkiraan
   * subtotal; pajak, service charge, dan pembulatan adalah kebenaran yang hanya boleh datang dari
   * satu tempat (`@osbpos/domain`). Menghitungnya dua kali = dua jawaban yang akan berbeda.
   */
  /**
   * ⭐⭐⭐ **Total HIDUP sambil mengetik** — SC & pajak, tanpa menyentuh server (Imron, 2026-08-06:
   * *"tombol calculate bill harus ada? bisakah automatis calculate?"*).
   *
   * ⛔ Yang TIDAK boleh otomatis: **menagih**. `Hitung Bill` di tier ber-server bukan aritmetika —
   * ia MENYIMPAN pesanannya ke server lalu memindahkan statusnya ke `billed`, dan status itu yang
   * mengunci baris, mengatur void/buka-kembali, serta menegakkan urutan "kirim dapur dulu".
   * Menagih otomatis tiap ketukan berarti pesanan lahir & terkunci selagi kasir masih menambah
   * item — lalu tiap tambahan berikutnya menuntut buka-kembali.
   *
   * ⇒ Yang dibuat otomatis **angkanya**, bukan perbuatannya. Mesinnya `@osbpos/domain`, kode yang
   *   SAMA dengan yang dipakai server & tier tablet — bukan rumus kedua yang akan menyimpang
   *   beberapa rupiah. (Bukti bahwa ia cocok: `/sync/sale` MENOLAK selisih sekecil apa pun antara
   *   hitungan tablet dan hitungan server.)
   *
   * ⚠️ `undefined` bila aturan pajak belum dibawa katalog — dan itu benar: angka tanpa pajak
   * terlihat wajar, tamu membayar kurang, dan yang menemukannya baru tutup buku.
   */
  const perkiraan = useMemo(() => {
    if (line.length === 0 || taxRules === undefined) return undefined;
    /*
     * ⛔⛔ **Pesanan ber-PROMO tak boleh diperkirakan.** Potongan dialokasikan per baris dengan
     * pembulatan eksak DI SERVER; menghitungnya ulang di sini melahirkan angka kedua yang berbeda
     * beberapa rupiah — dan yang muncul di layar justru total TANPA potongan, lebih besar dari yang
     * akan ditagih. Kasir yang membacakannya menagih tamu terlalu banyak.
     *
     * ⇒ Begitu promo menyentuh pesanan ini, satu-satunya angka yang sah datang dari `Hitung Bill`.
     */
    /*
     * ⚠️ **Berubah 2026-08-25.** Dulu perkiraan BERHENTI begitu promo dipakai — karena potongannya
     * hanya bisa dihitung server. Kini `assembleSale` menghitungnya sendiri dengan mesin bersama,
     * jadi yang tersisa cuma keadaan yang memang tak bisa dihitung di sini: promo yang diterapkan
     * lewat SERVER (tier server), yang potongannya sudah melekat di `bill` dari sana.
     */
    if (promoInfo !== null && promoTablet === null) return undefined;
    try {
      return assembleSale({
        catalog: { priceChannel, tax: taxRules },
        outletCode,
        deviceCode,
        /*
         * ⛔⛔ **Perkiraan tak boleh mengambil nomor.** Blok ini dihitung ulang tiap kali baris
         * berubah — tiap ketukan item. Mengambil nomor di sini membakar puluhan nomor per bill.
         *
         * ⚠️ Nilainya sengaja BUKAN nomor yang sah: dokumen perkiraan tak pernah diantrekan
         * (yang dikirim `assembled`), jadi kalau `PERKIRAAN` sampai muncul di laporan, itu bug
         * yang menyebut namanya sendiri — bukan bill yang menyamar jadi normal.
         */
        orderNo: 'PERKIRAAN',
        businessDate: props.businessDate ?? '',
        orderType: 'dine_in',
        ...(tanda === null ? {} : {
          parkKind: tanda.kind, parkLabel: tanda.label, guestCount: tanda.guests,
        }),
        ...(promoTablet === null ? {} : { promo: promoTablet }),
        lines: line.map((b) => ({
          code: b.code, qty: b.qty, price: b.price,
          discountAllowed: menu.find((m) => m.code === b.code)?.discountAllowed !== false,
          /*
           * ⭐⭐⭐ **Modifier ikut dihitung DAN ikut dikirim** (G-2, UAT 2026-08-26).
           * ⛔ Sebelum ini keduanya hilang: Es Kopi + Extra Shot tampil Rp 25.000 padahal 33.000,
           * dan yang tersimpan di server satu baris tanpa modifier sama sekali.
           * ⚠️ `modifierDelta` TERPISAH dari `price`: `price` naik ke server sebagai harga DASAR,
           * dan server menambahkan deltanya sendiri dari master — digabung di sini, ia dihitung dua kali.
           */
          ...(b.modifiers.length === 0 ? {} : { modifiers: b.modifiers }),
          modifierDelta: deltaModifier(b),
        })),
        paymentMethodCode: paymentMethods[0]?.code ?? 'CASH',
      });
    } catch {
      return undefined;
    }
  }, [line, taxRules, priceChannel, outletCode, deviceCode, props.businessDate, paymentMethods,
      promoInfo, tanda]);

  const computeBill = useCallback(async (
    knownId?: string,
    /**
     * ⭐ Promo yang BARU dipilih — dioper eksplisit karena `promoTablet` di sini masih nilai render
     * SEBELUMNYA. ⛔ Jebakan yang sama persis dengan dokumen tender di `pay()`: membaca state yang
     * belum sempat berlaku menghasilkan bill tanpa potongan, dan kasir menekan Promo dua kali.
     */
    promoPaksa?: PromoSpec,
  ) => {
    if (line.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      /*
       * ⭐⭐⭐ **Tier satu-tablet menghitung SENDIRI** — tak menyentuh server sama sekali sampai
       * pembayaran selesai (K13 §5d). Yang dipakai `@osbpos/domain`, kode yang sama persis dengan
       * mesin hitung portal, di atas aturan pajak yang dibawa katalog.
       *
       * ⛔ Kalau aturan pajaknya belum ada, ia MELEMPAR — bukan menghitung "seadanya". Angka tanpa
       * pajak terlihat wajar, tamu membayar kurang, dan yang menemukannya baru tutup buku.
       */
      if (tablet) {
        /* ⚠️ Yang BARU dipilih menang atas state — lihat catatan pada `promoPaksa`. */
        const promoDipakai = promoPaksa ?? promoTablet;
        const a = assembleSale({
          catalog: { priceChannel, ...(catalogVersion === undefined ? {} : { version: catalogVersion }),
            ...(taxRules === undefined ? {} : { tax: taxRules }) },
          outletCode,
          deviceCode,
          /* ⭐ Nama kasir ikut di dokumen — tanpa ini struk tier tablet menyebut nama PERANGKAT. */
          ...(staffSession() === null ? {} : {
            cashierCode: staffSession()?.staffCode ?? '',
            cashierName: staffSession()?.displayName ?? '',
          }),
          /*
           * ⛔ Tanggal usaha dari SESI KAS (`props.businessDate`), bukan jam tablet. Kalau sesinya
           * belum ada, `computeBill` di tier ini memang tak boleh jalan — bukan diganti hari ini.
           */
          /* ⭐ Satu nomor per bill — lihat `nomorBill`. */
          orderNo: (nomorBill.current ??= takeOrderNo(deviceCode, props.businessDate ?? '')),
          businessDate: props.businessDate ?? '',
          /* ⭐ Sesi kas SELALU ikut — di tier tablet ia yang menjadikan uangnya bisa dihitung. */
          ...(props.shiftId === null ? {} : { shiftId: props.shiftId }),
          orderType: 'dine_in',
          /* ⭐⭐⭐ Penanda ikut DOKUMEN (K31): tier tablet tak punya pesanan di server sampai
             dokumennya mendarat, jadi ini satu-satunya jalan penanda sampai ke dapur.
             ⛔ Dari REF — dokumen ini dirakit di lanjutan yang berjalan pada tick yang sama dengan
             pengisian penandanya, jadi state di sini masih milik render sebelumnya (G-3). */
          ...(tandaRef.current === null ? {} : {
            parkKind: tandaRef.current.kind,
            parkLabel: tandaRef.current.label,
            guestCount: tandaRef.current.guests,
          }),
          ...(promoDipakai === null ? {} : { promo: promoDipakai }),
          lines: line.map((b) => ({
            code: b.code, qty: b.qty, price: b.price,
            /* ⭐ Boleh-didiskon ikut: dasarnya harus disaring sama persis dengan server. */
            discountAllowed: menu.find((m) => m.code === b.code)?.discountAllowed !== false,
            /* ⭐ Komponen set menu ikut — tanpa ini paketnya mendarat tanpa isi. */
            ...(b.combo === undefined || b.combo.length === 0 ? {} : { comboSelections: b.combo }),
            /* ⭐⭐⭐ Modifier ikut DOKUMEN YANG DIKIRIM (G-2). ⛔ Sebelum ini hanya perkiraan di
               layar yang punya modifier — dokumen yang benar-benar mendarat tak pernah membawanya,
               jadi yang tersimpan permanen adalah baris tanpa modifier dan tanpa harganya. */
            ...(b.modifiers.length === 0 ? {} : { modifiers: b.modifiers }),
            modifierDelta: deltaModifier(b),
          })),
          paymentMethodCode: paymentMethods[0]?.code ?? 'CASH',
        });
        setAssembled(a);
        setPaidAmount('0.0000');
        setDone(false);
        /* ⚠️ Bill BARU ⇒ lacinya belum ditendang untuk penjualan ini. */
        laciTertendang.current = false;
        setSheetOpen(true);
        setBill({
          subtotalGross: a.subtotalGross,
          discountTotal: '0.0000',
          serviceChargeTotal: a.serviceChargeTotal,
          taxTotal: a.taxTotal,
          grandTotal: a.grandTotal,
          status: 'billed',
        });
        return;
      }

      /*
       * ⛔⛔ `knownId` ada ⇒ **JANGAN dorong ulang**. Dalam satu ketukan Bayar, `sendOrder` sudah
       * mendorong seluruh barisnya; memanggil `pushLines` lagi di tick yang sama membaca `line`
       * dari render sebelumnya — di sana `serverQty` masih nol, jadi baris yang sama TERKIRIM DUA
       * KALI dan tamu ditagih dobel. Tak ada satu galat pun yang muncul untuk itu.
       */
      const id = knownId ?? (await pushLines());
      setPaidAmount('0.0000');
      setDone(false);
      laciTertendang.current = false;
      /*
       * ⭐ Lembar dibuka SENDIRI saat bill jadi — itu satu-satunya momen ia wajib terlihat, dan
       * membiarkan kasir mencarinya di detik tamu menyerahkan uang adalah ketukan yang paling mahal.
       * ⛔ TIDAK dibuka tiap item ditambah: yang muncul tiap ketukan berhenti dibaca.
       */
      setSheetOpen(true);
      setBill(await api.bill(id, { actor: pelaku() }));
    } catch (e) {
      handleError(e, () => computeBill(knownId));
    } finally {
      setBusy(false);
    }
  }, [line, pushLines, handleError]);

  /**
   * ⭐⭐ Menerapkan promo, lalu **menghitung ulang bill dari SERVER**.
   *
   * ⛔ Layar tak pernah mengurangi totalnya sendiri: potongan dialokasikan per baris dengan
   * pembulatan eksak di server, dan menghitungnya ulang di sini akan melahirkan angka kedua yang
   * berbeda beberapa rupiah — persis jenis selisih yang tak bisa dijelaskan saat tutup buku.
   */
  const applyPromo = useCallback(async () => {
    if (promoCode.trim() === '') return;
    const kode = promoCode.trim().toUpperCase();

    /*
     * ⭐⭐⭐ **Tier satu-tablet menghitung SENDIRI** (2026-08-25). Sampai kini promo hanya bisa lewat
     * server, jadi tablet luring tak bisa memberi potongan sama sekali.
     *
     * ⛔ Yang membuatnya aman: potongannya dihitung `promoDiscount` — fungsi yang SAMA yang dipakai
     * server saat dokumennya mendarat. Dua salinan aturan pembulatan berarti tiap penjualan
     * berpromo ditolak `SYNC-409-VARIANCE`.
     */
    if (tablet) {
      const spec = promos.find((p) => p.code.toUpperCase() === kode);
      if (spec === undefined) {
        setError(t('cashier.promo.unknown', { code: kode }));
        return;
      }
      /*
       * ⛔⛔ **Promo bertanda `requires_approval` DITOLAK di tier ini** (K23). PIN penyelia
       * dibuktikan terhadap basis data, dan tablet luring tak punya satu pun. ⚠️ Dikatakan apa
       * adanya — menerimanya lalu gagal saat dokumennya mendarat berarti tamu sudah pergi membawa
       * potongan yang tak pernah sah.
       */
      if (spec.requiresApproval === true) {
        setError(t('cashier.promo.needsServer', { code: kode }));
        return;
      }
      const sebab = promoValidity(spec, props.businessDate ?? '', new Date());
      if (sebab !== null) {
        setError(t(`cashier.promo.invalid.${sebab}`, { code: kode }));
        return;
      }
      /* ⚠️ Belanja minimum diperiksa terhadap DASAR, bukan grand total — cermin server. */
      const dasar = line
        .filter((b) => menu.find((m) => m.code === b.code)?.discountAllowed !== false)
        .reduce((n, b) => n + Number(lineTotal(b.price, b.qty, deltaModifier(b))), 0);
      if (!promoMeetsMinimum(spec, money(dasar.toFixed(4)))) {
        setError(t('cashier.promo.belowMin', { code: kode }));
        return;
      }
      setPromoTablet(spec);
      setPromoInfo(t('cashier.promo.appliedOffline', { code: spec.code }));
      setPromoDialog(false);
      setPromoCode('');
      /*
       * ⭐ Bill DIHITUNG ULANG di tempat, ⛔ bukan dikosongkan. Mengosongkannya menutup seluruh
       * panel bayar — termasuk pesan "promo terpasang" yang baru saja ditulis — jadi kasir tak
       * pernah melihat konfirmasi dari perbuatannya sendiri.
       * ⚠️ Promonya DIOPER: `promoTablet` baru berlaku di render berikutnya.
       */
      void computeBill(undefined, spec);
      return;
    }

    if (orderId === null) return;
    setBusy(true);
    setError(null);
    try {
      /*
       * ⭐ Persetujuan penyelia ikut HANYA bila server sudah memintanya sekali. Mengirimkannya
       * duluan berarti kasir dimintai PIN penyelia untuk promo biasa — dan kebiasaan itu yang
       * membuat penyelia akhirnya menitipkan PIN-nya.
       */
      const r = await api.applyPromo(
        orderId,
        kode,
        approvalNeeded !== null && approvalCode.trim() !== '' && approvalPin !== ''
          ? { staffCode: approvalCode.trim(), pin: approvalPin }
          : undefined,
      );
      setPromoInfo(t('cashier.promo.applied', {
        code: r.promoCode, amount: formatRupiah(r.discountTotal),
      }));
      setPromoDialog(false);
      setPromoCode('');
      setApprovalNeeded(null);
      setApprovalCode('');
      setApprovalPin('');
      setBill(await api.bill(orderId, {
        actor: pelaku(),
      }));
    } catch (e) {
      const d = (e as { detail?: ApiError }).detail;
      /*
       * ⛔ BUKAN galat yang ditampilkan lalu selesai: 403 ini adalah **permintaan**, dan satu-satunya
       * jalan majunya adalah memanggil penyelia. Melemparkannya ke kotak galat merah membuat kasir
       * mengira promonya rusak — lalu ia memberi potongan manual yang tak tercatat sebagai promo.
       */
      if (d?.error_code === 'PROMO-403-NEEDS_APPROVAL') {
        const n = d.context?.['discountTotal'];
        setApprovalNeeded({ discountTotal: typeof n === 'string' ? n : '0' });
        setApprovalPin('');
        return;
      }
      /* PIN/izin penyelia salah: tetap di dialog persetujuan, jangan buang yang sudah diketik. */
      if (d?.error_code === 'AUTH-401-PIN' || d?.error_code === 'AUTH-403-NOT_APPROVER'
          || d?.error_code === 'AUTH-423-PIN_LOCKED') {
        setApprovalPin('');
        setError(`${d.error_code} · ${d.trace_id}`);
        return;
      }
      handleError(e, applyPromo);
    } finally {
      setBusy(false);
    }
  }, [orderId, promoCode, approvalNeeded, approvalCode, approvalPin, t, handleError]);

  const pay = useCallback(
    async (
      m: PaymentMethod, amount: string, reference?: string, tenderedAmount?: string,
      /* ⭐ Seam EDC mode MANUAL (K7) — satu objek, bukan parameter posisi yang terus memanjang. */
      edc?: { approvalCode?: string; cardLast4?: string },
    ) => {
      /*
       * ⭐ Tier tablet tak mencatat tender ke server saat tamu membayar — seluruh penjualan
       * berangkat SEKALI sesudah selesai (K13 §5d). Yang terjadi di sini cuma layar mengakui
       * uangnya sudah diterima; yang mengikatnya ke buku adalah `close` di bawah.
       *
       * ⛔⛔⛔ **Tendernya diselesaikan DI SINI, bukan saat bill dihitung.** `assembleSale`
       * dipanggil sebelum kasir memilih apa pun, jadi ia terpaksa memakai `paymentMethods[0]` —
       * dan sampai 2026-08-10 tak ada yang memperbaikinya kemudian: `m` dibuang begitu saja.
       * ⇒ penjualan QRIS mendarat di portal sebagai TUNAI. `tender_type` adalah klasifikasi
       * ekonomi (Guardrail #2) yang dipetakan ERP ke akun: kas bertambah untuk uang yang masih di
       * penyelenggara QRIS, dan lacinya "kurang" tiap tutup shift tanpa satu pun galat.
       */
      if (tablet) {
        setPaidAmount(amount);
        /*
         * ⛔⛔⛔ **Dokumen diselesaikan LEBIH DULU, dan dioper ke penutup secara eksplisit.**
         *
         * Sejak penutupan otomatis (2026-08-25) keduanya berlomba: `close()` membaca `assembled`
         * dari render yang SEDANG berjalan, sementara `setAssembled` baru berlaku di render
         * berikutnya. Menutup lebih dulu berarti yang diantrekan adalah dokumen SEBELUM tender
         * diselesaikan — metodenya kembali ke `paymentMethods[0]` dan uang yang diterima hilang.
         *
         * ⚠️ Itu persis regresi yang ditutup 2026-08-10 (penjualan QRIS tablet tercatat TUNAI,
         * dan ERP memetakan `tender_type` ke AKUN). Tak ada satu galat pun yang muncul untuknya —
         * hanya OB6 yang menangkapnya.
         */
        const lunas = assembled === null ? null : settleSale(assembled, {
          paymentMethodCode: m.code,
          amount,
          ...(tenderedAmount === undefined ? {} : { tenderedAmount }),
          ...(reference === undefined ? {} : { referenceNo: reference }),
          /* ⛔ Kosong TIDAK dikirim: `approval_code = ''` menulis kolom tanpa menambah satu pun
             jawaban, dan menghapus arti NULL ("tak dinyatakan"). */
          ...(edc?.approvalCode === undefined || edc.approvalCode === ''
            ? {} : { approvalCode: edc.approvalCode }),
          ...(edc?.cardLast4 === undefined || edc.cardLast4 === ''
            ? {} : { cardLast4: edc.cardLast4 }),
        });
        if (lunas !== null) setAssembled(lunas);
        /*
         * ⛔⛔ Tendangan laci DI-AWAIT sebelum menutup: `cetakStruk` membaca `laciTertendang` untuk
         * memutuskan apakah struk masih perlu menendang, dan yang belum selesai membacanya `false`
         * — lalu laci tertendang DUA kali. ⚠️ Aman dari menggantung: `printViaAgent` berbatas waktu.
         */
        await tendangLaci(m);
        /* ⚠️ Sisanya dari dokumen yang DITAGIHKAN, bukan katalog: angka yang dilihat tamu. */
        const sisa = Number(bill?.grandTotal ?? '0') - Number(amount);
        if (sisa <= 0 && lunas !== null) tutupRef.current?.(lunas);
        return;
      }
      if (orderId === null) return;
      setBusy(true);
      setError(null);
      try {
        const actor = pelaku();
        const r = await api.addTender(orderId, {
          paymentMethodCode: m.code,
          amount: amount,
          /* ⭐ Uang yang DISERAHKAN tamu — terpisah dari yang diterapkan ke bill (0060). Server
             menolak bila bukan tunai atau lebih kecil dari `amount`. */
          ...(tenderedAmount === undefined ? {} : { tenderedAmount }),
          ...(reference === undefined ? {} : { referenceNo: reference }),
          /* ⛔ Kosong TIDAK dikirim: `approval_code = ''` menulis kolom tanpa menambah satu pun
             jawaban, dan menghapus arti NULL ("tak dinyatakan"). */
          ...(edc?.approvalCode === undefined || edc.approvalCode === ''
            ? {} : { approvalCode: edc.approvalCode }),
          ...(edc?.cardLast4 === undefined || edc.cardLast4 === ''
            ? {} : { cardLast4: edc.cardLast4 }),
          actor: actor,
        });
        /* ⭐ Sesudah tendernya BENAR-BENAR tercatat, bukan sebelum: laci yang terbuka untuk
           tender yang ditolak server adalah laci yang terbuka tanpa uang masuk. */
        await tendangLaci(m);
        /*
         * ⭐⭐⭐ **Lunas ⇒ tutup sendiri.** Tombol Tutup dulu berdiri di antara "uang diterima" dan
         * "struk keluar" — satu ketukan tambahan di depan tamu yang sudah mengulurkan tangan,
         * untuk keputusan yang tak punya pilihan lain.
         * ⛔ HANYA saat sisanya nol: bagi-bayar (tunai sebagian, sisanya QRIS) wajib tetap terbuka
         * sampai tender terakhir, dan menutupnya lebih awal berarti bill lunas yang kurang bayar.
         * ⚠️ `remaining` dari SERVER — layar tak menghitung sisa sendiri.
         */
        if (Number(r.remaining) <= 0) tutupRef.current?.();
        // `remaining` datang dari SERVER — layar tak menghitung sisa sendiri.
        setPaidAmount(
          `${String(Number(bill?.grandTotal.split('.')[0] ?? '0') - Number(r.remaining.split('.')[0] ?? '0'))}.0000`,
        );
      } catch (e) {
        handleError(e, () => pay(m, amount, reference));
      } finally {
        setBusy(false);
      }
    },
    [orderId, bill, tablet, assembled, handleError, tendangLaci],
  );

  const close = useCallback(async (
    /**
     * ⭐ Dokumen yang SUDAH diselesaikan tendernya — dioper eksplisit oleh penutupan otomatis.
     * ⛔ Tanpa ini ia membaca `assembled` dari render yang sedang berjalan, yaitu dokumen SEBELUM
     * tendernya diselesaikan (lihat catatan panjang di `pay`).
     */
    lunasPaksa?: AssembledSale,
  ) => {
    /*
     * ⭐⭐⭐ **Tier satu-tablet: antrekan DULU, baru layar boleh bilang "lunas".**
     *
     * ⛔ Urutannya yang menentukan, bukan isinya. Kalau layar mengaku lunas sementara dokumennya
     * belum tersimpan, satu tablet yang mati di detik itu menghapus penjualan yang uangnya SUDAH
     * masuk laci — dan tak ada satu pun jejak bahwa ia pernah ada. `enqueueSale` sengaja sinkron
     * supaya "sudah tersimpan?" tak pernah jadi pertanyaan di titik ini.
     *
     * ⚠️ Yang diantrekan dokumen yang DITAGIHKAN (`assembled`), bukan hasil rakit ulang: merakit
     * ulang di sini bisa menghasilkan angka berbeda dari yang dilihat tamu kalau katalognya
     * kebetulan tersegarkan di antara bill dan bayar.
     */
    if (tablet) {
      const doc = lunasPaksa ?? assembled;
      if (doc === null) return;
      /* ⭐ Diingat SEBELUM `assembled` dikosongkan — struk dirender SERVER dari id yang sama, dan
         id itu pula kunci idempotensi `/sync/sale` (K13). */
      const idJual = String((doc.doc as Record<string, unknown>)['orderId'] ?? '');
      enqueueSale(doc.doc);
      setQueued(pendingCount());
      setDone(true);
      setAutoPrint({ kind: 'idle' });
      setLines([]);
      setBill(null);
      setAssembled(null);
      nomorBill.current = null;
      /* ⚠️ Promo melekat pada BILL, bukan pada terminal — yang tak dilepas akan ikut ke tamu
         berikutnya, dan potongan yang tak diminta siapa pun tak punya jejak yang menjelaskannya. */
      setPromoTablet(null);
      setPromoInfo(null);
      setKitchenNotice(null);
      /* ⭐ Dorong SESUDAH layar aman: kalau jaringan mati, antreannya yang menanggung. */
      void flush().finally(() => {
        setQueued(pendingCount());
        setReview(needsReviewCount());
        /* ⛔ SESUDAH dorongan: sebelum dokumennya mendarat, tak ada yang bisa dirender server. */
        if (idJual !== '') void cetakStruk(idJual);
      });
      return;
    }
    if (orderId === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.close(orderId, {
        actor: pelaku(),
      });
      setDone(true);
      /*
       * ⭐ Diingat SEBELUM `orderId` dikosongkan — inilah satu-satunya pegangan kasir kalau
       * ternyata metode bayarnya salah pilih dan tamunya masih di depan meja.
       */
      setLastPaidId(orderId);
      setAutoPrint({ kind: 'idle' });
      /*
       * ⭐⭐⭐ **Dapur menyusul SESUDAH uang diterima** (quick service). ⛔ Kegagalannya TIDAK
       * ditelan: tamu sudah membayar, jadi tiket yang tak berangkat adalah makanan yang tak pernah
       * dimasak — kebalikan dari kegagalan cetak, yang cuma soal kertas.
       */
      if (payFirst && mustSend) {
        void api.sendOrder(orderId, { actor: pelaku() })
          .then(async (r) => { await cetakTiketDapur(orderId, r.sendRound); })
          .catch((e: Error) => { setError(pesanGalat(e)); });
      }
      void cetakStruk(orderId);
      setLines([]);
      setBill(null);
      setOrderId(null);
      setTanda(null);
      props.onOrderChanged?.(null);
    } catch (e) {
      const d = (e as { detail?: { error_code: string; trace_id: string } }).detail;
      /*
       * ⭐⭐ **Jaringan mati tepat saat kasir menekan TUTUP.** Uangnya sudah di laci dan tamunya
       * sudah pergi; kehilangan penjualannya di sini adalah kehilangan yang paling mahal, karena
       * tak ada satu pun jejak yang tersisa untuk dibandingkan.
       *
       * ⚠️ Hanya untuk kegagalan **TRANSPORT** (`detail` kosong ⇒ tak pernah sampai server).
       * Penolakan server punya `error_code` — itu keputusan, bukan kecelakaan, dan mengantrekannya
       * berarti mengirim ulang selamanya sesuatu yang memang ditolak.
       */
      if (d === undefined && bill !== null) {
        enqueue({
          recordId: orderId,
          // ⛔ STABIL: dipakai ulang di tiap percobaan. Berubah = penjualan ganda di laporan.
          requestId: `close-${orderId}`,
          outletCode,
          deviceCode,
          /* ⭐ Nama kasir ikut di dokumen — tanpa ini struk tier tablet menyebut nama PERANGKAT. */
          ...(staffSession() === null ? {} : {
            cashierCode: staffSession()?.staffCode ?? '',
            cashierName: staffSession()?.displayName ?? '',
          }),
          /*
           * ⛔⛔ Tanggal usaha SESI KAS. Yang diantrekan di sini mendarat di portal BERJAM-JAM
           * kemudian — kalau tanggalnya diambil dari jam tablet saat antre, penjualan pagi buta
           * masuk hari kemarin, dan settlement hari itu sudah menolaknya.
           */
          businessDate: props.businessDate ?? '',
          payload: {
            order_no: `Q/${orderId.slice(-8)}`,
            outlet_code: outletCode,
            business_date: props.businessDate ?? '',
            order_type: 'dine_in', entry_channel: 'pos', price_channel: priceChannel,
            guest_count: 1, status: 'paid',
            subtotal_gross: bill.subtotalGross,
            discount_total: bill.discountTotal,
            service_charge_total: bill.serviceChargeTotal,
            tax_total: bill.taxTotal,
            grand_total: bill.grandTotal,
            lines: line.map((l) => ({
              product_name: l.name,
              qty: `${String(l.qty)}.000000`,
              unit_price_gross: l.price,
              discount_amount: '0.0000',
              line_total: lineTotal(l.price, l.qty),
            })),
          },
        });
        setQueued(pendingCount());
        setDone(true);
        setLines([]);
        setBill(null);
        setOrderId(null);
      setTanda(null);
        props.onOrderChanged?.(null);
      } else {
        handleError(e, () => close());
      }
    } finally {
      setBusy(false);
    }
    // ⚠️ `bill` & `line` WAJIB jadi dependensi: tanpa itu penutupan memakai tangkapan lama, dan
    //    jalur antrean membaca `bill === null` lalu membuang penjualan yang justru ingin dijaga.
    //
    // ⛔⛔ `assembled` juga — dan itu baru ketahuan 2026-08-10 (OB6). Sejak `pay()` MENGUBAH
    //    dokumen (metode bayar & uang diterima diselesaikan di titik bayar), tangkapan lama berarti
    //    yang diantrekan adalah dokumen sebelum tender diselesaikan: metodenya kembali ke bawaan
    //    `paymentMethods[0]` dan uang yang diterima hilang. Tak ada gejalanya di layar — angkanya
    //    benar semua; yang salah cuma yang mendarat di portal.
  }, [orderId, bill, line, assembled, outletCode, deviceCode, priceChannel, props, handleError,
      cetakStruk]);

  /* ⚠️ Dipasang tiap render — `close` adalah closure baru tiap kali, dan yang lama memegang
     tangkapan `bill`/`assembled` yang sudah basi. */
  tutupRef.current = (lunas) => { void close(lunas); };

  /**
   * ⭐ **Membatalkan bill yang sudah dibuat di server.**
   *
   * ⚠️ Inilah yang menutup lubang lama: dulu menyunting baris sesudah "Hitung Bill" cuma
   * mengosongkan tampilan bill, sementara pesanannya **tetap hidup di server** — memegang kunci
   * meja, memegang nomor, dan tak pernah ditutup siapa pun. Sekarang penyuntingan dikunci selama
   * ada pesanan, dan satu-satunya jalan kembali adalah pembatalan yang BERALASAN dan tercatat.
   */
  const voidOrder = useCallback(
    async (reasonCode: string, reasonNote?: string) => {
      if (orderId === null) return;
      setBusy(true);
      setError(null);
      try {
        await api.voidOrder(orderId, {
          reasonCode,
          ...(reasonNote === undefined ? {} : { reasonNote }),
        });
        setOrderId(null);
      setTanda(null);
        props.onOrderChanged?.(null);
        setBill(null);
        setPaidAmount('0.0000');
      } catch (e) {
        handleError(e, () => voidOrder(reasonCode, reasonNote));
      } finally {
        setBusy(false);
      }
    },
    [orderId, props, handleError],
  );

  /*
   * ⛔ Selama pesanan hidup di server, baris TAK BOLEH disunting di layar. Dua salinan yang
   * menyimpang (layar vs server) adalah cara paling halus membuat struk dan laporan berbeda.
   */
  /**
   * ⭐ Outlet yang **harus mengirim dulu**: ada dapur/bar yang dituju (kapabilitas
   * `kitchen_routing`), atau pesanan menempel pada meja dan hidup berjam-jam sebelum ditagih
   * (mode jual `table_service`).
   *
   * ⚠️ Keduanya BERDIRI SENDIRI. Quick service pun mengirim ke dapur — pesanannya tetap harus
   * turun ke griller dan ke printer bar; yang tak ada di sana cuma mejanya.
   */
  const mustSend = capabilities.includes('kitchen_routing') || saleMode === 'table_service';

  /** Berapa banyak yang belum sampai ke dapur — dipakai tombol KIRIM dan peringatan keluar. */
  const unsentCount = line.reduce((n, x) => n + Math.max(0, x.qty - x.sentQty), 0);

  /**
   * ⭐⭐⭐ **Quick service: UANG DULUAN** (Imron, 2026-08-22: *"di mode quick service harusnya
   * bukan tombol send tetapi pay"*).
   *
   * Itulah yang membedakan counter service dari table service, dan bedanya bukan tata letak
   * melainkan URUTAN: tamu memesan di konter, **membayar**, lalu pesanannya turun ke dapur. Di
   * table service kebalikannya — makanan datang dulu, bill menyusul berjam-jam kemudian.
   *
   * ⛔ Sampai hari ini `mustSend` memaksa dua ketukan di QSR: KIRIM lalu HITUNG BILL, dengan
   * "Hitung Bill" MATI sampai yang pertama ditekan. Kasir yang antreannya panjang menekan tombol
   * yang menyala, jadi urutannya "benar" — tapi ia mengirim makanan ke dapur untuk tamu yang
   * belum membayar apa pun, dan tak ada satu pun gerbang yang menahannya kalau tamu itu pergi.
   *
   * ⚠️ Kirimnya TIDAK hilang — ia cuma berhenti jadi pekerjaan kasir. `bayarLangsung` mengirim
   * dulu (gerbang §10 di server tetap berlaku apa adanya), baru menagih. Yang dihapus satu
   * ketukan, ⛔ bukan satu kontrol.
   */
  const payFirst = saleMode === 'quick_service';



  /* ⚠️ Perpindahan sibuk → kosong yang membersihkan "Nanti", bukan timer: yang menandai bill
     selesai adalah layar yang kembali kosong, dan itu momen yang sama yang menampilkan ajakan. */
  const idleSebelumnya = useRef(false);
  useEffect(() => {
    if (idle && !idleSebelumnya.current) setSyncSnoozed(false);
    idleSebelumnya.current = idle;
  }, [idle]);

  const syncAsk = syncPending && idle && !syncSnoozed && !syncing;

  /**
   * ⭐⭐ Satu ketukan: kirim (kalau outletnya memang berdapur) lalu hitung bill.
   *
   * ⛔ `await` hasil kirimnya diperiksa: menagih sesudah kirim yang GAGAL berarti tamu membayar
   * makanan yang tak pernah sampai ke dapur — dan galat kirimnya sudah tertelan layar.
   */
  /**
   * ⭐⭐⭐ **Batalkan TAGIHAN** — kembali menyunting, tanpa membunuh pesanannya.
   *
   * Imron (2026-08-23): *"ketika tekan tombol pay yang pertama, tidak ada tombol cancel-nya."*
   * Benar — dan satu-satunya jalan mundur adalah **void**, yang membunuh pesanannya. Tamu yang cuma
   * ingin menambah satu es teh memaksa kasir mengetik ulang seluruh bill di depan antrean.
   *
   * ⚠️ Tier tablet: billnya memang tak pernah hidup di server (K13), jadi membatalkan cukup
   * membuang hitungannya di layar. ⛔ Tier ber-server: server yang memutuskan, dan ia MENOLAK bila
   * sudah ada tender (`ORDER-409-TENDER_EXISTS`).
   */
  const batalTagihan = useCallback(async () => {
    if (tablet) {
      setBill(null); setAssembled(null); nomorBill.current = null;
      setPaidAmount('0.0000'); return;
    }
    if (orderId === null) { setBill(null); return; }
    setBusy(true);
    setError(null);
    try {
      await api.cancelBill(orderId);
      setBill(null);
      setPaidAmount('0.0000');
      /* ⛔ `orderId` DIPERTAHANKAN: pesanannya masih hidup dan barisnya utuh — itu seluruh maksud
         "batal tagihan" dibanding void. Mengosongkannya di sini akan meninggalkan pesanan yang
         menggantung di server tanpa satu pun layar yang memegangnya. */
    } catch (e) {
      handleError(e, () => void batalTagihan());
    } finally {
      setBusy(false);
    }
  }, [orderId, tablet, handleError]);

  /**
   * ⭐⭐⭐ **Penanda diminta SEBELUM permintaannya berangkat** (Imron, 2026-08-23: *"waktu ketik
   * tombol pay, harusnya langsung muncul dialog, tidak perlu tekan tombol lagi"*).
   *
   * ### ⛔ Kenapa dulu tidak begini
   * Percobaan pertama memasang dialog wajib sebelum TIAP Kirim/Bayar ⇒ 52 dari 165 uji kasir merah.
   * Sebabnya bukan ujinya: gerbangnya menyala juga di outlet yang **tak berdapur sama sekali**,
   * tempat penanda tak pernah dibutuhkan siapa pun.
   *
   * ⇒ Pembedanya `mustSend` — outlet yang memang menerbitkan tiket dapur. Di situ penandanya pasti
   *   diminta server, jadi menanyakannya sekarang **menghemat satu penolakan**, bukan menambah satu
   *   langkah. Di outlet tanpa dapur, tak ada yang berubah sama sekali.
   *
   * ⚠️ Gerbang server tetap berdiri (`ORDER-409-NO_TAG`) — ini kenyamanan di depannya, bukan
   * penggantinya. Kalau penandanya hilang lewat jalur lain, penolakan server tetap membuka dialog.
   */
  const dahuluiTanda = useCallback((lanjut: () => void): void => {
    if (!mustSend || table !== null || tanda !== null) { lanjut(); return; }
    bukaDialogTanda(lanjut);
  }, [mustSend, table, tanda, bukaDialogTanda]);

  /**
   * ⭐⭐⭐ **Quick service: HITUNG BILL saja — dapur menyusul SESUDAH bayar** (Imron, 2026-08-25:
   * *"saat ini status item sudah terkirim padahal blm ada pembayaran"*).
   *
   * ⛔ Sampai kini ia mengirim ke dapur DULU baru menagih — kebalikan dari niat yang tertulis
   * persis di atas `payFirst`: *"tamu memesan di konter, MEMBAYAR, lalu pesanannya turun ke
   * dapur"*. Sebabnya struktural, bukan kelalaian: `sendToKitchen` menolak pesanan ber-status
   * `billed`/`paid`, jadi satu-satunya urutan yang MUNGKIN adalah kirim-dulu. Gerbangnya sudah
   * dilonggarkan untuk `paid` saja (lihat `order.ts`), dan urutannya kini bisa mengikuti niatnya.
   */
  const bayarLangsung = useCallback(async () => {
    await computeBill();
  }, [computeBill]);

  /**
   * ⭐⭐ **Keluar = ganti orang, BUKAN tutup kasir.**
   *
   * ⛔ Sesi kas sengaja **tidak** ikut ditutup: laci dan `expected_cash` milik SESI, bukan milik
   * orang yang kebetulan sedang berdiri di depannya. Memaksa tutup sesi tiap pergantian pelayan
   * berarti setoran dihitung ulang belasan kali semalam — dan yang paling mungkin terjadi
   * bukanlah kepatuhan, melainkan satu orang yang login untuk semua.
   *
   * ⚠️ Baris yang belum terkirim akan hilang ⇒ dikonfirmasi dulu. Yang sudah terkirim aman: ia
   * sudah hidup di server, bukan di layar ini.
   */
  /**
   * ⭐⭐⭐ **Pelaku = staf yang BENAR-BENAR login**, bukan nama yang dipahat.
   *
   * ⛔ Sampai 2026-08-06 berkas ini mengirim `{ code: 'ARENA-001', name: 'Kasir' }` di TUJUH
   * tempat. Akibatnya terbukti di data sungguhan: tenant `arena` punya tiga staf (`IMRON`,
   * `FADLI`, `ALYSIA`) sementara SELURUH pesanan tercatat dibuka oleh "Kasir".
   *
   * ⚠️ Yang menentukan sekarang tetap SERVER (`actorFromStaff` menimpa apa pun yang dikirim dari
   * sini) — ini bukan lapisan keamanan melainkan kejujuran: klien tak boleh mengirim nama yang ia
   * tahu salah, walau server akan memperbaikinya.
   */
  const pelaku = useCallback(() => {
    const s = staffSession();
    return { actorKind: 'staff' as const, code: s?.staffCode ?? '', name: s?.displayName ?? '' };
  }, []);

  const logout = useCallback(() => {
    if (unsentCount > 0 && !confirm(t('cashier.admin.logoutWarning'))) return;
    if (orderId !== null) void api.releaseHold(orderId).catch(() => undefined);
    logoutStaff();
    props.onLoggedOut?.();
  }, [orderId, props, t, unsentCount]);

  /**
   * ⭐ **Kembali ke pilih meja = selesai menyunting.**
   *
   * Pelayan sering membuka meja, mengetik separuh, lalu kembali untuk melihat meja lain — dan
   * pesanannya belum dikirim. Tanpa pelepasan kunci di sini, mejanya tersandera sampai kasir
   * memakai paksa-buka, dan paksa-buka yang dipakai setiap hari berhenti berarti apa-apa.
   *
   * ⚠️ Kegagalan melepas SENGAJA diabaikan: pelayan tetap boleh berpindah. Kunci yang tak terlepas
   * masih punya jalan keluar (paksa-buka); pandangan yang menolak berganti karena jaringan sedang
   * putus tidak punya.
   */
  /**
   * ⭐⭐⭐ **Tahan pesanan** — menyisihkannya supaya kasir bisa melayani tamu berikutnya.
   *
   * ⛔ Kunci sesi-sunting DILEPAS server saat menahan: pesanan yang tertahan tapi masih terkunci
   * di terminal ini tak bisa dipanggil kasir lain, dan tersandera sampai ada yang memaksa buka.
   *
   * ⚠️ Layar dikosongkan SESUDAH server menjawab. Mengosongkannya lebih dulu membuat kasir mengira
   * pesanannya tersimpan padahal permintaannya gagal — dan bill itu hilang tanpa jejak di layar.
   */

  /**
   * Menyimpan penanda dari dialog — dan **selalu lewat server**, bukan cuma disimpan di layar.
   *
   * ⛔ Penanda yang hidup di layar saja adalah penanda yang tak sampai ke dapur; itu persis
   * keadaan yang membuat dua belas kartu di KDS tak bisa dibedakan.
   */
  const simpanTanda = useCallback(async (): Promise<void> => {
    const d = tahanTanda;
    if (d === null) return;
    const teks = d.text.trim();
    const pax = Number(d.guests);
    const id = orderIdRef.current;
    try {
      /*
       * ⚠️ Bill BELUM ada di server (belum satu baris pun terkirim) ⇒ penandanya disimpan di layar
       * dulu, lalu ikut saat `openOrder`. Menolak di sini berarti kasir tak bisa menuliskan nama
       * tamu sebelum menekan item pertama — urutan yang justru paling alami.
       */
      if (id !== null && (d.kind === 'table' || teks !== '')) {
        await api.tagOrder(id, {
          kind: d.kind,
          ...(d.kind === 'table' ? {} : { label: teks }),
          guestCount: pax,
        });
      }
      if (d.kind === 'table' || teks !== '') {
        setTanda({
          kind: d.kind,
          label: d.kind === 'table' ? (table?.code ?? '') : teks,
          guests: pax,
        });
        /* ⚠️ Nomor naik HANYA bila nama bawaannya dipakai apa adanya — yang mengetik "Bu Rina"
           tak menghabiskan nomor, kalau tidak urutannya melompat tanpa tamu yang hilang. */
        if (d.kind === 'name') commitGuestName(props.businessDate ?? '', teks);
      }
      setTahanTanda(null);
      if (d.mode === 'park') {
        await tahan(d.kind === 'table' || teks !== '' ? d.kind : undefined,
          d.kind === 'table' ? undefined : teks);
      }
      const lanjut = lanjutSesudahTanda.current;
      lanjutSesudahTanda.current = null;
      lanjut?.();
    } catch (e) {
      setError(pesanGalat(e));
    }
  }, [tahanTanda, table, props.businessDate]);

  const tahan = useCallback(async (kind?: 'table' | 'name' | 'queue', label?: string) => {
    try {
      /*
       * ⛔⛔ **Menahan MENYIMPAN dulu.** Sampai di sini baris masih hidup di layar saja — pesanan
       * baru lahir di server saat dikirim atau ditagih. Menahan tanpa menyimpan berarti bill itu
       * hilang begitu tab ditutup, baterai habis, atau kasir menekan muat-ulang: yang tersisa cuma
       * satu baris di daftar tahan yang isinya kosong.
       */
      /* ⛔ Tanpa penyusulan penanda — `park` di baris berikutnya membawanya sendiri. */
      const id = await pushLines(false);
      await api.park(id, {
        ...(kind === undefined ? {} : { kind }),
        ...(label === undefined || label === '' ? {} : { label }),
      });
      setTahanTanda(null);
      setOrderId(null);
      setTanda(null);
      setLines([]);
      setBill(null);
      setTable(null);
      setSelected(null);
      /* ⭐ Sama alasan dengan `backToTables`: tanpa ini, "Lunas" milik pembayaran SEBELUMNYA
         tertinggal berdampingan dengan meja yang baru saja ditahan. */
      setDone(false);
      setLastPaidId(null);
      setAutoPrint({ kind: 'idle' });
      setRefundInfo(null);
      /*
       * ⛔⛔ Ditarik ULANG di sini, EKSPLISIT. Menyandarkannya pada `orderId` yang kebetulan
       * berubah adalah kebetulan, bukan aturan — dan billnya yang hilang tanpa muncul di daftar
       * adalah kegagalan yang terbaca kasir sebagai "hold tidak bisa".
       */
      void muatTertahan();
    } catch (e) {
      setError(pesanGalat(e));
    }
  }, [pushLines, muatTertahan, setOrderId]);

  /**
   * ⭐⭐ **Jumlah tertahan dimuat sejak BOOT**, bukan cuma saat daftarnya dibuka.
   *
   * ⛔ Sebelum ini `parked` hanya terisi di dalam `bukaTertahan`, jadi penghitung di layar selalu
   * berbunyi **"Tertahan (0)"** sampai seseorang mengkliknya — angka yang tak pernah benar, dan
   * karena itu tak pernah memberi tahu apa pun. Penghitung yang selalu nol lebih buruk daripada
   * tak ada penghitung: ia mengajari orang bahwa angkanya memang tak usah dibaca.
   *
   * ⚠️ Gagal memuat DIABAIKAN — bill yang sedang dikerjakan tak boleh terganggu oleh daftar yang
   * cuma informatif.
   */

  const bukaTertahan = useCallback(() => {
    void api.parked(outletCode, props.businessDate ?? '')
      .then(setParked)
      .catch((e: Error) => { setError(pesanGalat(e)); });
    setView('parked');
  }, [outletCode, props.businessDate]);

  /** ⛔ Memanggil kembali MENGAMBIL kunci: kalau terminal lain memegangnya, server menolak. */
  const panggil = useCallback(async (o: ParkedOrder) => {
    try {
      await api.resume(o.orderId);
      /*
       * ⛔ Baris DIBACA ULANG dari server. Menampilkan bill kosong padahal servernya berisi tiga
       * item membuat kasir memasukkannya lagi — tamu ditagih dua kali, dan tak satu pun angka di
       * sistem terlihat salah.
       */
      const isi = await api.orderLines(o.orderId);
      setLines(isi.map((x) => ({
        /* ⭐ Baris dari server sudah punya identitas sendiri — pakai itu, jangan buat yang kedua. */
        key: x.lineId,
        code: x.code,
        name: x.name,
        price: x.unitPrice,
        qty: Number(x.qty),
        sentQty: Number(x.sentQty),
        /* ⭐ Inilah yang membedakan "sudah di server" dari "sudah di dapur" — lihat `Line.serverQty`. */
        serverQty: Number(x.qty),
        serverLineId: x.lineId,
        /*
         * ⚠️ `modifiers` (masukan) sengaja KOSONG, `modifierLabels` (tampilan) TERISI.
         *
         * Pilihannya sudah tersimpan di server sebagai baris anak; mengirimkannya lagi lewat
         * `pushLines` akan MENGGANDAKANNYA. Yang dibutuhkan layar cuma menampilkannya kembali —
         * dan itu bedanya "apa yang dikirim" dengan "apa yang dilihat".
         */
        modifiers: [],
        modifierLabels: x.modifierLabels,
      })));
      setOrderId(o.orderId);
      setTandaTersimpan({ orderId: o.orderId, kind: o.kind, label: o.label });
      if (o.tableCode !== null) setTable({ code: o.tableCode, guests: 0 });
      setBill(null);
      setView('menu');
    } catch (e) {
      setError(pesanGalat(e));
    }
  }, []);

  const backToTables = useCallback(() => {
    if (orderId !== null) void api.releaseHold(orderId).catch(() => undefined);
    setView('tables');
    setTable(null);
    setSelected(null);
    /*
     * ⭐⭐ **Panel "Lunas" TAK PERNAH dibersihkan sebelum ini** — `done`/`lastPaidId` hanya
     * direset saat bill BERIKUTNYA dihitung (lihat `pushLines`), bukan saat kasir pergi dari
     * meja ini. Akibatnya: bayar meja 5, tekan "← Meja", meja 5 dibuka lagi nanti ⇒ layar pesanan
     * kosong yang BENAR (⭐ untuk pesanan baru) tampil BERSAMA sisa "Lunas" + cetak struk + buka
     * kembali + refund milik pembayaran LAMA — dua konteks bertumpuk yang terlihat berantakan
     * (dilaporkan Imron, 2026-09-08).
     *
     * ⛔ Bukan sekadar kosmetik: "Buka kembali" & "Refund" yang lingering menunjuk `lastPaidId`
     * TETAP FUNGSIONAL walau kasir sudah pindah konteks — kasir bisa membuka kembali bill lama
     * tanpa sadar sedang berdiri di meja yang berbeda.
     */
    setDone(false);
    setLastPaidId(null);
    setAutoPrint({ kind: 'idle' });
    setRefundInfo(null);
  }, [orderId]);

  return (
    <div className="cashier" data-sheet={sheetOpen ? 'open' : 'closed'}>
      {/*
        ⭐⭐⭐ **Tombol mengambang: buka/tutup panel pesanan** (Imron, 2026-08-25).

        ⛔ Ia MENYEBUT jumlah & totalnya, bukan cuma ikon keranjang. Tombol yang tak menyebut apa-apa
        memaksa kasir membukanya hanya untuk memastikan itemnya masuk — yaitu dua ketukan untuk
        pertanyaan yang bisa dijawab tanpa satu pun.

        ⚠️ Selalu dirender; CSS yang menyembunyikannya di atas lebar HP. Memagarinya dengan
        `window.innerWidth` di JS berarti tablet yang diputar membaca lebar yang sudah basi.
      */}
      <div className="cashier-fab" data-testid="order-fab">
        {/*
          ⛔⛔ **Dua tombol, bukan satu yang berubah arti.** Satu tombol yang kadang membuka panel
          dan kadang menagih adalah kontrol yang berbohong: kasir menekan tempat yang sama dan
          mendapat dua akibat berbeda, dan yang salah baru ketahuan sesudah tamu membayar.

          ⇒ KIRI membuka panel (jumlah & total — jawaban atas *"itemnya sudah masuk?"*),
            KANAN mengerjakan aksi utama, persis tombol yang sama di dalam panel.
        */}
        <button
          type="button"
          className="fab-peek"
          data-testid="order-fab-peek"
          aria-expanded={sheetOpen}
          onClick={() => { setSheetOpen((v) => !v); }}
        >
          <span className="fab-count">{jumlahItem}</span>
          <span className="fab-total">{formatRupiah(totalBerjalan)}</span>
        </button>
        {/*
          ⭐ Aksi utama IKUT di sini supaya HP tak menuntut ketukan tambahan: tanpa ini kasir harus
          membuka panel dulu tiap transaksi, hanya untuk menjangkau tombol yang sama.
          ⚠️ Gerbangnya DISALIN persis dari tombol di dalam panel — dua tombol yang mengerjakan hal
          yang sama dengan syarat berbeda adalah cara tercepat membuat salah satunya salah.
        */}
        {/*
          ⛔⛔ **Selalu ada selama masih ada baris** (Imron, 2026-08-25: *"setelah tutup panel …
          tidak bisa kembali lagi (text bayar hilang)"*).
          Dulu ia hilang begitu bill terhitung — jadi kasir yang menutup panelnya melihat pil yang
          cuma menyebut angka, tanpa satu kata pun yang mengatakan bagaimana melanjutkan. Pilnya
          memang masih bisa diketuk, tapi tombol yang tak menyebut apa-apa bukan jalan keluar.

          ⭐ Labelnya SAMA di kedua keadaan, dan tujuannya juga sama: menuju pembayaran. Yang
          berbeda cuma apakah billnya perlu dihitung dulu — dan itu bukan urusan kasir.
        */}
        {line.length > 0 && (
          <button
            type="button"
            className="fab-act"
            data-testid="order-fab-act"
            disabled={busy || (bill === null && mustSend && !payFirst && unsentCount > 0)}
            onClick={() => {
              setSheetOpen(true);
              if (bill !== null) return;
              dahuluiTanda(() => {
                if (payFirst) void bayarLangsung();
                else void computeBill();
              });
            }}
          >
            {/*
              ⛔⛔ **Katanya harus cocok dengan perbuatannya** (Imron, 2026-08-25: *"tekan tombol
              floating button bayar, bukan proses pembayaran, hanya collapse containernya"*).

              Sebelum ini ia berbunyi "Bayar" di KEDUA keadaan — padahal saat billnya sudah ada ia
              cuma MEMBUKA panel. Tombol yang menjanjikan pembayaran lalu cuma menggeser panel
              adalah tombol yang berbohong, dan yang membacanya sedang berdiri di depan tamu.
            */}
            {busy
              ? t('common.loading')
              : bill !== null
                ? t('cashier.action.openPay')
                : t(payFirst ? 'cashier.action.pay' : 'cashier.action.bill')}
          </button>
        )}
      </div>
      {/*
        ⛔ **SATU pita, bukan dua.** Sebelumnya ada pita hijau tersendiri di atas yang isinya cuma
        satu tombol ("← Meja" atau nama staf) — satu jalur penuh untuk satu tombol, di layar yang
        justru harus memberi tempat sebanyak mungkin kepada menu dan bill.
      */}
      <header className="cashier-top">
        <h1>{t('cashier.title')}</h1>
        {props.businessDate !== undefined && (
          <span className="business-date" data-testid="business-date">
            {t('cashier.businessDate')} <b>{props.businessDate}</b>
          </span>
        )}
        {/*
          ⛔⛔ **Salinan katalog HARUS terlihat.** Menampilkan menu kemarin tanpa tanda membuat kasir
          menjual harga yang mungkin sudah berubah tanpa pernah tahu — dan yang menemukannya adalah
          tamu yang membandingkan struk dengan daftar harga di dinding.

          ⚠️ Tanggalnya ikut disebut, bukan cuma kata "luring": "salinan" tanpa umur tak bisa
          dinilai. Salinan sejam lalu dan salinan minggu lalu adalah dua keadaan yang berbeda jauh.
        */}
        {/*
          ⛔⛔ **Sesi dari salinan wajib mengaku.** Izinnya berumur sampai 7 hari dan tak tahu
          apa-apa soal pencabutan yang terjadi selagi tablet luring. Sesi salinan yang menyamar
          jadi sesi biasa membuat orang percaya pada jaminan yang sedang tidak berlaku.
        */}
        {staffSession()?.offline === true && (
          <span className="warn" data-testid="sesi-luring">{t('cashier.offlineSession')}</span>
        )}
        {/*
          ⭐⭐ **Penghitung tertahan pindah ke PITA ATAS** (Imron, 2026-08-06: *"kenapa masih ada
          disitu, memakan ruang"*).

          Ia bukan aksi atas pesanan yang sedang dibuka — ia **pekerjaan yang belum selesai**, dan
          tempat yang benar untuk itu sebaris dengan tanggal usaha & kesehatan server: hal-hal yang
          harus terbaca SEKILAS tanpa memakan ruang menu.

          ⛔ Disembunyikan saat NOL, dan itu bukan sekadar hemat ruang: penghitung nol adalah kabar
          "tak ada pekerjaan", dan kabar itu tak perlu tempat tetap di layar. Tombol "Tahan" di
          baris aksi sudah cukup memberi tahu bahwa menahan itu mungkin.
        */}
        {parked.length > 0 && (
          <button
            type="button"
            className="chip-tertahan"
            data-testid="to-parked"
            onClick={bukaTertahan}
          >
            {t('cashier.parked.title', { n: parked.length })}
          </button>
        )}
        {catalogStale !== null && (
          <span className="warn" data-testid="katalog-salinan">
            {t('cashier.catalogCached', { at: catalogStale.slice(0, 16).replace('T', ' ') })}
          </span>
        )}
        {/*
          ⭐⭐⭐ **Tombol SYNC** — muncul HANYA saat portal sudah menekan Send Data dan terminal ini
          belum menariknya. Alur yang sama dengan VTEC: pemberitahuan dulu, kasir yang memutuskan.

          ⛔ Tak pernah menarik sendiri. Menu yang berganti di tengah antrean bukan kenyamanan;
          ia merampas kendali dari orang yang sedang berdiri di depan tamu.
          ⚠️ Baris pesanan yang sudah masuk TIDAK ikut berubah — harganya dibekukan di baris saat
          item ditambahkan (`order_line.unit_price_gross`), jadi SYNC di tengah bill aman.
        */}
        {/*
          ⭐⭐⭐ **Batas jualan luring terlampaui** — dan ia WAJIB terlihat sebelum kasir menekan
          apa pun, bukan muncul sebagai penolakan sesudah item diketuk di depan tamu.

          ⚠️ Muncul juga saat masih ada bill berjalan (yang tak ditahan): kasir perlu tahu bahwa
          bill ini yang terakhir bisa dibuka, supaya ia sempat memanggil orang sebelum antreannya
          menumpuk.
        */}
        {luringTerlaluLama && (
          <span className="warn" data-testid="offline-limit">
            {t('cashier.offlineLimit.badge', { days: hariTerputus, max: offlineMaxDays })}
          </span>
        )}
        {/*
          ⭐⭐⭐ **Cari bill naik ke bilah atas** (Imron, 2026-08-25: *"search bill untuk re print
          belum ada. saat printer macet, dan butuh kertas bill ini wajib ada"*).

          Fiturnya SUDAH ada — di balik ☰ → "Cari bill". Tapi ia tak ditemukan, dan fitur yang tak
          ditemukan sama saja tak ada. ⛔ Yang membuatnya mahal: ia justru dibutuhkan saat printer
          macet dan tamu berdiri menunggu strukmya — momen paling buruk untuk menjelajahi menu.

          ⚠️ Tetap ADA juga di menu admin: memindahkannya berarti mematahkan hafalan orang yang
          sudah terbiasa, demi memperbaiki masalah orang yang belum.
        */}
        <button
          type="button"
          className="chip-find"
          data-testid="find-bill-top"
          title={t('cashier.find.hint')}
          onClick={() => { setFindOpen(true); }}
        >
          {t('cashier.find.button')}
        </button>
        {syncPending && (
          <button
            type="button"
            className="chip-sync"
            data-testid="sync-katalog"
            disabled={syncing}
            title={t('cashier.sync.hint')}
            onClick={() => {
              setSyncing(true);
              void muatKatalog().finally(() => { setSyncing(false); });
            }}
          >
            {syncing
              ? t('cashier.sync.busy')
              : /* ⭐ Menyebut sudah berapa lama menunggu — "3 jam" bisa dinilai, titik merah tidak. */
                `${t('cashier.sync.button')}${
                  menitSejak(syncSince) === null ? '' : ` · ${t('cashier.sync.behind', {
                    n: menitSejak(syncSince) ?? 0 })}`}`}
          </button>
        )}
        {/* ⭐ Hanya tier tablet yang punya antrean luring — spanduknya wajib mengatakan yang benar. */}
        <HealthIndicator status={status} queues={props.tier === 'tablet'} />
        {/*
          ⭐ Nama staf = identitas SEKALIGUS jalan ke sesi kas — gerakan yang sama dengan di
          dashboard meja. Kasir tak perlu belajar dua cara untuk satu tujuan.
        */}
        {props.onSession !== undefined && (
          /*
           * ⚠️ **Labelnya ikut disebut** (Imron, 2026-08-22: *"belum ada close session dan close
           * outlet/store"* — padahal keduanya ADA, tepat di balik tombol ini).
           *
           * `AdminMenu` sengaja TIDAK memuat sesi kas: dua jalan ke satu layar membuat separuh
           * kasir menghafal jalan yang salah. ⛔ Tapi keputusan itu menuntut jalan yang SATU itu
           * mengatakan ke mana ia pergi — dan nama orang tak mengatakan apa pun. ⇒ bukan menambah
           * pintu kedua, melainkan memberi nama pintu yang sudah ada.
           */
          <button
            type="button"
            className="staff-chip"
            data-testid="to-session"
            onClick={props.onSession}
          >
            <span className="staff-chip-name">{staffSession()?.displayName ?? ''}</span>
            <span className="staff-chip-hint">{t('cashier.sessionChip')}</span>
          </button>
        )}
        {/*
          ⭐ Fungsi langka & terkendali pindah ke ☰ (laci, batalkan bill, sesi kas, setelan).
          Pita atas menyisakan yang harus terbaca SEKILAS: judul, tanggal usaha, kesehatan.
        */}
        <AdminMenu
          outletCode={outletCode}
          deviceCode={deviceCode}
          shiftId={props.shiftId ?? null}
          orderId={orderId}
          /* ⚠️ `slow` tetap dianggap DARING: lambat masih bisa mengambil ulang katalog; yang tak
             bisa cuma `down`. Menahan pada `slow` membuat perawatan mustahil di jaringan outlet
             yang memang selalu lambat. */
          online={status.server !== 'down' && status.server !== 'unknown'}
          onVoidOrder={(r, n) => void voidOrder(r, n)}
          onLogout={logout}
          /* ⚠️ Ditarik ulang dulu: daftar yang dibuka dari salinan poll terakhir bisa memuat item
             yang sudah dinyalakan terminal sebelah semenit lalu. */
          onFindBill={() => { setFindOpen(true); }}
          onSalesToday={() => { setSalesOpen(true); }}
          onPrinterSetup={() => { setPrinterOpen(true); }}
          onSoldOut={() => {
            void muatSoldOut().then((items) => { setSoldOutReminder(items ?? soldOutList ?? []); });
          }}
          {...(props.businessDate === undefined ? {} : { businessDate: props.businessDate })}
        />
      </header>

      {/*
        ⭐ Dialog modifier melayang di atas SELURUH layar, bukan di dalam kolom menu: pertanyaannya
        harus dijawab sebelum apa pun berlanjut, dan dialog yang bisa diabaikan akan diabaikan.
      */}
      {modFor !== null && (
        <ModifierPicker
          productName={modFor.name}
          groups={modifierGroups.filter((g) => g.productCode === modFor.code)}
          formatPrice={formatRupiah}
          soldOut={soldOutMods}
          /* ⭐ Tekan-tahan opsi ⇒ tandai/cabut HABIS — gerakan yang SAMA dengan kartu menu, karena
             kasir tak boleh menghafal dua cara untuk satu maksud. */
          onHoldOption={(groupCode, optionCode, nama) => {
            setHoldFor({ code: `${groupCode}/${optionCode}`, name: nama } as MenuItem);
          }}
          onCancel={() => { setModFor(null); }}
          onConfirm={(sel) => {
            const label = sel.map((x) => {
              if (x.freeText !== undefined) return x.freeText;
              const g = modifierGroups.find(
                (y) => y.productCode === modFor.code && y.groupCode === x.groupCode,
              );
              return g?.options.find((o) => o.code === x.optionCode)?.name ?? x.optionCode ?? '';
            });
            tambahBaris(modFor, sel, label);
            setModFor(null);
          }}
        />
      )}

      {tahanTanda !== null && (
        /*
         * ⭐ Tandanya BOLEH kosong: kasir yang sedang sibuk tak dipaksa mengarang nama, dan server
         * memberi nomor antrean berikutnya. Yang tak boleh adalah jenis yang isinya tak cocok —
         * "meja" tanpa meja jadi baris tanpa identitas di daftar tahan.
         */
        <div className="dialog park-ask" role="dialog" data-testid="park-dialog">
          {/*
            ⛔ Kalimat "kosongkan saja bila buru-buru — sistem memberi nomor antrean" DIBUANG
            (Imron, 2026-08-25). Dua sebab: sejak quick service mengisi `guest_xx` sendiri, kotaknya
            tak pernah kosong lagi — jadi saran itu menyarankan hal yang tak lagi terjadi. Dan di HP
            ia mendorong tombol Simpan turun, yaitu satu-satunya tombol yang harus mudah dijangkau.
          */}
          <h2>{t('cashier.parked.ask')}</h2>
          <div className="park-kinds">
            {(['queue', 'name', 'table'] as const).map((k) => (
              <button
                key={k}
                type="button"
                className={tahanTanda.kind === k ? 'park-kind on' : 'park-kind'}
                data-testid={`park-kind-${k}`}
                disabled={k === 'table' && table === null}
                onClick={() => { setTahanTanda({ ...tahanTanda, kind: k, text: '' }); }}
              >
                {t(`cashier.parked.kind.${k}`)}
              </button>
            ))}
          </div>
          {tahanTanda.kind === 'table' ? (
            <p data-testid="park-table">{table?.code ?? t('cashier.parked.noTable')}</p>
          ) : (
            <input
              className="park-input"
              data-testid="park-label"
              /*
               * ⭐ `select()`, ⛔ bukan sekadar `focus()`: nama bawaan `guest_01` yang tersorot bisa
               * langsung ditimpa dengan mengetik. Kursor yang cuma berkedip di ujungnya menuntut
               * kasir menghapus sembilan huruf lebih dulu.
               */
              ref={refLabelTanda}
              maxLength={40}
              placeholder={tahanTanda.kind === 'name'
                ? t('cashier.parked.tagName')
                : t('cashier.parked.tagQueue')}
              value={tahanTanda.text}
              onChange={(e) => { setTahanTanda({ ...tahanTanda, text: e.target.value }); }}
            />
          )}
          {/*
            ⭐⭐ **Jumlah tamu WAJIB, di kedua mode** (Imron, 2026-08-23). Angkanya penyebut
            *average per cover* — laporan yang paling sering dipakai menilai kinerja outlet. Penyebut
            nol membuat seluruh laporan itu bohong tanpa satu angka pun terlihat salah.
          */}
          <label className="park-pax">
            {t('cashier.tag.paxLabel')}
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={999}
              data-testid="park-pax"
              ref={refPaxTanda}
              value={tahanTanda.guests}
              onChange={(e) => { setTahanTanda({ ...tahanTanda, guests: e.target.value }); }}
            />
          </label>
          <div className="dialog-actions">
            <button
              type="button"
              data-testid="park-cancel"
              onClick={() => { setTahanTanda(null); lanjutSesudahTanda.current = null; }}
            >
              {t('cashier.parked.close')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="park-simpan"
              /*
               * ⚠️ Teks kosong DIBOLEHKAN saat MENAHAN — server yang memberi nomor antrean
               * berikutnya, dan kasir yang sedang sibuk tak boleh dipaksa mengarang tanda (PA1).
               * ⛔ Saat cuma MENANDAI, teksnya wajib: menandai tanpa tanda tak berbuat apa-apa.
               */
              disabled={
                Number(tahanTanda.guests) < 1 ||
                (tahanTanda.mode === 'tag' && tahanTanda.kind !== 'table'
                  && tahanTanda.text.trim() === '')
              }
              onClick={() => void simpanTanda()}
            >
              {t(tahanTanda.mode === 'park' ? 'cashier.parked.button' : 'common.save')}
            </button>
          </div>
        </div>
      )}

      {printerOpen && (
        <PrinterSetup
          onClose={() => { setPrinterOpen(false); }}
          /* ⭐ Lencana printer diperiksa ulang seketika: yang baru memasang berhak melihat
             hasilnya tanpa menunggu denyut 5 detik berikutnya. */
          onAdopted={() => {
            void agentHealth().then((st) => {
              setPrinter(st === 'ready' ? 'healthy'
                : st === 'no-printer' || st === 'rejected' ? 'slow' : 'down');
              setPrinterState(st);
            });
          }}
        />
      )}

      {salesOpen && (
        <SalesToday
          outletCode={outletCode}
          {...(props.businessDate === undefined ? {} : { businessDate: props.businessDate })}
          onClose={() => { setSalesOpen(false); }}
        />
      )}

      {findOpen && (
        <BillSearch
          outletCode={outletCode}
          deviceCode={deviceCode}
          {...(props.businessDate === undefined ? {} : { businessDate: props.businessDate })}
          onClose={() => { setFindOpen(false); }}
          /*
           * ⭐ Menutup pencarian saat refund dibuka: dua dialog bertumpuk membuat kasir kehilangan
           * jejak mana yang sedang ia kerjakan — dan ini dialog yang menyangkut uang.
           */
          onRefund={(id) => { setFindOpen(false); setRefundFor(id); }}
        />
      )}

      {refundFor !== null && (
        <RefundDialog
          orderId={refundFor}
          shiftId={props.shiftId ?? null}
          onClose={() => { setRefundFor(null); }}
          onDone={(total) => {
            setRefundFor(null);
            /* ⭐ Angka SERVER yang ditampilkan, bukan perkiraan layar — lihat `RefundDialog`. */
            setRefundInfo(t('cashier.refund.done', { amount: formatRupiah(total) }));
          }}
        />
      )}

      {/*
        ⭐ Konfirmasi tekan-tahan. ⛔ Ada, bukan langsung berubah: tekan-tahan bisa terjadi karena
        jari berhenti di layar, dan mematikan menu tanpa sengaja baru ketahuan saat ada yang
        memesannya.
      */}
      {holdFor !== null && (
        <div className="dialog" role="dialog" data-testid="soldout-dialog">
          <h2>{holdFor.name}</h2>
          <div className="dialog-actions">
            <button type="button" data-testid="soldout-cancel" onClick={() => { setHoldFor(null); }}>
              {t('cashier.soldOut.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="soldout-confirm"
              onClick={() => {
                const kode = holdFor.code;
                const next = !soldOutCodes.has(kode);
                setHoldFor(null);
                void ubahSoldOut(kode, next);
              }}
            >
              {soldOutCodes.has(holdFor.code)
                ? t('cashier.soldOut.restock')
                : t('cashier.soldOut.mark')}
            </button>
          </div>
        </div>
      )}

      {/*
        ⭐⭐ Pengingat saat sesi kas dibuka — tandanya bertahan lintas hari, jadi inilah satu-satunya
        momen yang bisa mengatakannya sebelum ada tamu. Tiap baris bisa dinyalakan langsung dari
        sini: pengingat yang cuma memberitahu tanpa jalan keluar akan ditutup tanpa dibaca.
      */}
      {/*
        ⭐⭐⭐ **Ajakan SYNC** (Imron, 2026-08-25: *"sync harus diketahui orang outlet. jangan diam2"*).

        Tarik otomatis DITOLAK, dan alasannya benar: SYNC yang terjadi sendiri adalah SYNC yang tak
        diketahui siapa pun di outlet. ⇒ yang berubah bukan siapa yang menekan, melainkan **bunyinya**
        — dari lencana kecil yang bisa diabaikan seharian jadi ajakan yang berdiri di layar kosong.

        ⛔ Hanya saat layar KOSONG: di tengah bill ia akan menutupi pekerjaan orang yang sedang
        berdiri di depan tamu, dan itu persis yang K25 hindari.
      */}
      {syncAsk && (
        <div className="dialog" role="dialog" data-testid="sync-ask">
          <h2>{t('cashier.sync.askTitle')}</h2>
          <p>
            {menitSejak(syncSince) === null
              ? t('cashier.sync.hint')
              : t('cashier.sync.askBody', { n: menitSejak(syncSince) ?? 0 })}
          </p>
          <div className="dialog-actions">
            <button
              type="button"
              data-testid="sync-ask-now"
              onClick={() => {
                setSyncing(true);
                void muatKatalog().finally(() => { setSyncing(false); });
              }}
            >
              {t('cashier.sync.now')}
            </button>
            {/* ⚠️ "Nanti" menyembunyikan sampai bill berikutnya selesai — ⛔ tak pernah selamanya. */}
            <button
              type="button"
              className="ghost"
              data-testid="sync-ask-later"
              onClick={() => { setSyncSnoozed(true); }}
            >
              {t('cashier.sync.later')}
            </button>
          </div>
        </div>
      )}
      {soldOutReminder !== null && (
        <div className="dialog" role="dialog" data-testid="soldout-reminder">
          <h2>{t('cashier.soldOut.reminder', { count: soldOutReminder.length })}</h2>
          {soldOutReminder.length === 0 && (
            <p className="empty" data-testid="soldout-none">{t('cashier.soldOut.none')}</p>
          )}
          <ul className="soldout-list">
            {soldOutReminder.map((it) => (
              <li key={it.productCode}>
                <span className="soldout-name">{it.productName}</span>
                <span className="soldout-since">
                  {t('cashier.soldOut.since', { days: hariSejak(it.businessDate, props.businessDate ?? '') })}
                </span>
                <button
                  type="button"
                  data-testid={`soldout-restock-${it.productCode}`}
                  onClick={() => {
                    setSoldOutReminder((v) =>
                      (v ?? []).filter((x) => x.productCode !== it.productCode));
                    void ubahSoldOut(it.productCode, false);
                  }}
                >
                  {t('cashier.soldOut.restock')}
                </button>
              </li>
            ))}
          </ul>
          <div className="dialog-actions">
            <button
              type="button"
              className="action-primary"
              data-testid="soldout-reminder-close"
              onClick={() => { setSoldOutReminder(null); }}
            >
              {t('cashier.soldOut.keep')}
            </button>
          </div>
        </div>
      )}

      <section className="cashier-menu" aria-label={t('cashier.addItem')}>
        {/*
          ⭐⭐ **Set menu memakai container yang SAMA dengan menu & meja** (Imron, 2026-08-07).
          Yang berganti cuma isi kolom kiri; bill di kanan tak pernah bergerak — dan itu penting
          justru saat paket sedang disusun, karena di situlah kasir paling perlu melihat apa yang
          sudah dipesan tamu.
        */}
        {view === 'combo' && comboFor !== null ? (
          <ComboPicker
            combo={comboFor}
            groups={comboGroups.filter((g) => g.comboCode === comboFor.code)}
            formatPrice={formatRupiah}
            onCancel={() => { setComboFor(null); setView('menu'); }}
            onConfirm={(selections, labels, price) => {
              setBill(null);
              /* ⚠️ Harga yang dibawa baris = harga paket SESUDAH upcharge, bukan harga dasar —
                 kalau tidak, angka di bill lebih kecil dari yang akan ditagih server. */
              tambahBaris({ ...comboFor, price }, [], labels, selections);
              setComboFor(null);
              setView('menu');
            }}
          />
        ) : view === 'parked' ? (
          <ParkedList
            rows={parked}
            formatPrice={formatRupiah}
            onResume={(o) => { void panggil(o); }}
            onClose={() => { setView('menu'); }}
          />
        ) : view === 'tables' ? (
          <div className="tables-pane">
            {/*
              ⭐ Panggilan tamu duduk DI ATAS meja, bukan di layar tersendiri: layar pulang pelayan
              adalah pandangan ini, dan panggilan yang harus dicari di tempat lain adalah panggilan
              yang terlewat.
            */}
            <GuestCallPanel outletCode={outletCode} />
            <TableDashboard
              outletCode={outletCode}
              onOpenTable={(tableCode, guestCount) => {
                setTable({ code: tableCode, guests: guestCount });
                setView('menu');
              }}
              // ⚠️ Masuk ke pesanan yang sudah ada memakai jalur yang sama — layar tak perlu tahu
              //    bedanya "meja baru" dan "meja yang sudah berisi".
              onEnterOrder={() => { setView('menu'); }}
            />
          </div>
        ) : (
        <>
        {saleMode === 'table_service' && (
          /*
           * ⭐⭐ **Tombol kembali BESAR** (Imron): di mode pilih menu, pelayan harus selalu tahu
           * jalan pulang tanpa mencarinya. Chip kecil di pita atas terlalu mudah terlewat justru
           * saat tangan penuh nampan dan mata di tamu.
           */
          <button
            type="button"
            className="back-big"
            data-testid="to-tables"
            onClick={backToTables}
          >
            {t('cashier.backToTables')}
            {table !== null && <span className="back-big-table">{table.code}</span>}
          </button>
        )}
        <input
          className="cari"
          data-testid="search"
          placeholder={t('cashier.searchMenu')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {menu.length === 0 && (
          <p className="empty" data-testid="menu-empty">{t('cashier.menuEmpty')}</p>
        )}
        <MenuBrowser
          menu={menu}
          search={search}
          department={department}
          category={category}
          onDepartment={setDepartment}
          onCategory={setCategory}
          onPick={add}
          formatPrice={formatRupiah}
          soldOut={soldOutCodes}
          onHold={setHoldFor}
        />
        </>
        )}
      </section>

      <aside
        className="cashier-bill"
        aria-label={t('cashier.title')}
        /* ⚠️ Atribut, bukan kelas kondisional: CSS yang memutuskan kapan ia BERARTI (media query). */
        data-sheet={sheetOpen ? 'open' : 'closed'}
        data-testid="cashier-bill"
      >
        {/*
          ⭐ Tombol tutup lembar — hanya terlihat di HP (CSS). ⛔ Tanpa jalan pulang yang JELAS,
          lembar yang menutupi seluruh layar terbaca sebagai layar yang tersangkut.
        */}
        <button
          type="button"
          className="sheet-close"
          data-testid="sheet-close"
          onClick={() => { setSheetOpen(false); }}
        >
          {t('cashier.sheet.close')}
        </button>
        {/*
          ⭐⭐⭐ **Penanda di ATAS bill, bukan di dalam menu tersembunyi** (Imron, 2026-08-23).

          Ia harus terbaca tanpa dicari: kasir yang menyebut *"Meja 5"* ke tamu dan juru masak yang
          membaca *"Meja 5"* di KDS harus melihat kata yang sama, di tempat yang sama, sepanjang
          bill itu hidup. ⛔ Penanda yang hanya muncul saat dialognya dibuka akan terlupa — dan yang
          terlupa muncul lagi sebagai kartu tanpa nama di dapur.
        */}
        {line.length > 0 && (
          /*
           * ⭐⭐ **Tiap bagian bisa diketuk SENDIRI** (Imron, 2026-08-25: *"ketika guestname bisa
           * edit nama; ketika guest number bisa edit jumlah tamu"*).
           *
           * ⛔ Satu tombol untuk seluruh pita berarti kasir yang cuma mau membetulkan jumlah tamu
           * tetap mendarat di kotak NAMA — lalu mengetiknya ulang atau salah menimpanya. Yang
           * diketuk harus jadi yang siap diketik.
           *
           * ⚠️ Keadaan KOSONG tetap satu tombol lebar: belum ada apa pun untuk dipilah, dan sasaran
           * sentuh yang dipecah tanpa isi cuma memperkecil peluang kena.
           */
          table !== null || tanda !== null ? (
            <div className="bill-tag" data-testid="bill-tag-row">
              <span className="bill-tag-kind">
                {t(`cashier.tag.kind.${table !== null ? 'table' : (tanda?.kind ?? 'name')}`)}
              </span>
              {/*
                ⚠️ Penanda MEJA tak punya label yang bisa disunting — kodenya milik denah lantai,
                bukan ketikan kasir. ⇒ mengetuknya membuka dialog pada JUMLAH TAMU, satu-satunya
                yang memang bisa diubah di sana.
              */}
              <button
                type="button"
                className="bill-tag-label"
                data-testid="bill-tag-label"
                onClick={() => { bukaDialogTanda(undefined, table !== null ? 'pax' : 'label'); }}
              >
                {table !== null ? table.code : (tanda?.label ?? '')}
              </button>
              <button
                type="button"
                className="bill-tag-pax"
                data-testid="bill-tag-pax"
                onClick={() => { bukaDialogTanda(undefined, 'pax'); }}
              >
                {t('cashier.tag.pax', { n: tanda?.guests ?? table?.guests ?? 1 })}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="bill-tag kosong"
              data-testid="bill-tag"
              onClick={() => { bukaDialogTanda(); }}
            >
              {/* ⛔ Keadaan kosong DISEBUT, bukan disembunyikan: penanda yang tak terlihat adalah
                 penanda yang terlupa, dan yang terlupa muncul lagi sebagai kartu tanpa nama di
                 dapur — persis keluhan yang melahirkan seluruh fitur ini. */}
              <span className="bill-tag-empty" data-testid="bill-tag-empty">
                {t('cashier.tag.empty')}
              </span>
            </button>
          )
        )}
        <div className="receipt">
        {line.length === 0 ? (
          <p className="empty" data-testid="bill-empty">{t('cashier.emptyOrder')}</p>
        ) : (
          <ul className="line" data-testid="line">
            {/*
              ⭐ Barisnya SENDIRI yang jadi tombol pilih — sasaran seukuran baris, bukan ikon kecil
              di ujungnya. Jari di tablet meleset pada sasaran 36px, dan meleset di daftar bill
              berarti menyunting baris yang salah.
            */}
            {line.map((b) => (
              <li key={b.key}>
                <button
                  type="button"
                  className={`line-row${selected === b.key ? ' selected' : ''}`}
                  data-testid={`line-${b.code}`}
                  aria-pressed={selected === b.key}
                  onClick={() => { setSelected((cur) => (cur === b.key ? null : b.key)); }}
                >
                  <span className="line-qty">{b.qty}×</span>
                  <span className="line-name">
                    {b.name}
                    {/*
                      ⭐⭐ **Tanda TERKIRIM di barisnya sendiri.**
                      Tanpa ini pelayan tak punya cara membedakan "sudah di dapur" dari "baru
                      diketuk" selain mengingatnya — dan yang diingat akan salah tepat pada bill
                      yang paling ramai. Separuh terkirim ditulis apa adanya, bukan dibulatkan.
                    */}
                    {b.sentQty > 0 && (
                      <span className="line-sent" data-testid={`line-sent-${b.code}`}>
                        {b.sentQty >= b.qty
                          ? t('cashier.line.sent')
                          : t('cashier.line.sentPartial', {
                              sent: b.sentQty,
                              fresh: b.qty - b.sentQty,
                            })}
                      </span>
                    )}
                  </span>
                  <span className="line-total">
                    {formatRupiah(lineTotal(b.price, b.qty, deltaModifier(b)))}
                  </span>
                  {/*
                    ⭐ Modifier tampil sebagai baris anak MENJOROK — bentuk yang sama dengan
                    tiket dapur dan struk. Menyembunyikannya membuat "kenapa harganya beda?"
                    dan "kok pedas?" tak bisa dijawab dari layar yang sedang dilihat.
                  */}
                  {b.modifierLabels.length > 0 && (
                    <span className="line-mods" data-testid={`line-mods-${b.code}`}>
                      {b.modifierLabels.map((m) => (
                        <em key={m}>· {m}</em>
                      ))}
                    </span>
                  )}
                </button>
                {selected === b.key && (
                  <LineContextBar
                    unitLabel={`${String(b.qty)} × ${formatRupiah(b.price)}`}
                    qty={b.qty}
                    minQty={Math.max(1, b.sentQty)}
                    removable={b.sentQty === 0}
                    {...(b.sentQty >= b.qty
                      ? { lockedReason: t('cashier.line.sentLocked') }
                      : {})}
                    onIncrement={() => { increment(b.key); }}
                    onDecrement={() => { decrement(b.key); }}
                    onRemove={() => { removeLine(b.key); }}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        <dl className="summary">
          <dt>{t('cashier.summary.subtotal')}</dt>
          <dd data-testid="subtotal">
            {bill === null ? formatRupiah(String(subtotalKasar)) : formatRupiah(bill.subtotalGross)}
          </dd>
          {/*
            ⭐ Angka yang ditampilkan: BILL bila sudah ditagih, kalau belum PERKIRAAN lokal.
            ⚠️ Perkiraan DITANDAI (`total-perkiraan`) — angka yang menyamar jadi tagihan resmi
            membuat kasir membacakannya ke tamu sebelum promo & pembulatan server ikut dihitung.
          */}
          {(bill ?? perkiraan) !== undefined && (bill ?? perkiraan) !== null && (
            <>
              <dt>{t('cashier.summary.serviceCharge')}</dt>
              <dd data-testid="sc">
                {formatRupiah((bill ?? perkiraan)!.serviceChargeTotal)}
              </dd>
              <dt>{t('cashier.summary.pb1')}</dt>
              <dd data-testid="pb1">{formatRupiah((bill ?? perkiraan)!.taxTotal)}</dd>
              <dt className="total">
                {t('cashier.summary.total')}
                {bill === null && (
                  <span className="hint" data-testid="total-perkiraan">
                    {' '}{t('cashier.summary.estimate')}
                  </span>
                )}
              </dt>
              <dd className="total" data-testid="total">
                {formatRupiah((bill ?? perkiraan)!.grandTotal)}
              </dd>
            </>
          )}
        </dl>
        </div>
        {/* Tepi robek: bentuk yang sama dengan kertas di tangan tamu. */}
        <div className="receipt-edge" aria-hidden="true" />

        {error !== null && (
          <p className="error" data-testid="error">{error}</p>
        )}

        {bill === null ? (
          /*
           * ⭐⭐ **Dua aksi berdampingan di outlet yang mengirim** (pola yang sama di dua referensi
           * yang dikirim Imron: PLACE + PAY berdampingan).
           *
           * ⛔ "Hitung Bill" DIMATIKAN selama masih ada baris yang belum terkirim — §10: baris
           * wajib melewati `order.sent` sebelum bill. Menagih makanan yang belum sampai ke dapur
           * adalah urutan yang salah, dan sekali dibiasakan ia jadi cara baku outlet itu bekerja.
           */
          <div className={mustSend && !payFirst ? 'action-pair' : undefined}>
            {/*
              ⛔ Di quick service tombol KIRIM tak ditampilkan sama sekali — bukan dimatikan.
              Tombol mati mengundang pertanyaan "kenapa"; tombol yang tak ada menyatakan bahwa
              mengirim memang bukan pekerjaan kasir di sini. Kirimnya tetap terjadi, dirangkai
              `bayarLangsung`.
            */}
            {mustSend && !payFirst && (
              <button
                type="button"
                className="action-primary"
                data-testid="send-order"
                disabled={unsentCount === 0 || busy}
                onClick={() => { dahuluiTanda(() => { void sendOrder(); }); }}
              >
                {busy ? t('common.loading') : t('cashier.action.sendToKitchen')}
              </button>
            )}
            {/*
              ⭐ "Tahan" duduk di sini sejak 2026-08-06 — bersama aksi lain atas bill yang sama.
              ⚠️ Mati saat bill kosong: menahan pesanan yang belum berisi apa pun cuma melahirkan
              baris kosong di daftar tahan yang kelak tak bisa dijelaskan siapa pun.
            */}
            <button
              type="button"
              className="action-secondary"
              data-testid="park"
              disabled={line.length === 0 || busy}
              /*
               * ⭐⭐ **Yang sudah bertanda tak ditanya lagi** (Imron, 2026-08-22).
               *
               * ⛔ Kasir memanggil "Bu Rina", menambah satu es teh, lalu menahannya lagi — dan
               * dialog yang sama muncul menanyakan tanda yang sudah dijawab. Yang buru-buru akan
               * menekan Simpan tanpa membaca, dan tandanya berganti jadi nomor antrean baru
               * sementara tamunya masih dipanggil "Bu Rina".
               *
               * ⚠️ Servernya yang MEMPERTAHANKAN tanda lama (`parkOrder` tanpa `kind`), bukan layar
               * yang mengirimkannya balik: nomor itu milik pesanan, dan klien yang harus
               * mengingatnya kelak lupa tanpa gejala.
               */
              onClick={() => {
                if (tandaTersimpan?.orderId === orderId) { void tahan(); return; }
                setTahanTanda({ mode: 'park', kind: 'queue', text: '', guests: '1' });
              }}
            >
              {t('cashier.parked.button')}
            </button>
            {/*
              ⚠️ `data-testid` SENGAJA tetap `compute-bill` walau labelnya berganti jadi "Bayar":
              yang berubah urutannya, bukan tombolnya — dan mengganti testid membuat seluruh uji
              yang menyebutnya terlihat seperti fitur yang hilang.
            */}
            <button
              type="button"
              className={mustSend && !payFirst ? 'action-secondary' : 'action-primary'}
              data-testid="compute-bill"
              disabled={line.length === 0 || busy || (mustSend && !payFirst && unsentCount > 0)}
              title={mustSend && !payFirst && unsentCount > 0
                ? t('cashier.action.billNeedsSend') : undefined}
              onClick={() => {
                dahuluiTanda(() => {
                  if (payFirst) void bayarLangsung();
                  else void computeBill();
                });
              }}
            >
              {busy
                ? t('common.loading')
                : t(payFirst ? 'cashier.action.pay' : 'cashier.action.bill')}
            </button>
          </div>
        ) : (
          <>
          {/*
            ⭐ Tombol promo hanya muncul SESUDAH bill ada: promo dihitung dari baris yang sudah
            tercatat di server, dan tombol yang bisa ditekan lebih awal cuma menghasilkan
            penolakan yang membingungkan.
          */}
          <button
            type="button"
            className="action-secondary"
            data-testid="promo-buka"
            disabled={busy}
            onClick={() => { setPromoDialog(true); }}
          >
            {t('cashier.promo.button')}
          </button>

          {promoInfo !== null && (
            <p className="info" data-testid="promo-info">{promoInfo}</p>
          )}
          {kitchenNotice !== null && (
            /* ⚠️ Barisnya SENDIRI: pemberitahuan rute & konfirmasi promo dua hal yang tak
               berhubungan, dan yang berbagi satu tempat akan saling menimpa. */
            <p className="warn" data-testid="kitchen-notice">{kitchenNotice}</p>
          )}

          {promoDialog && (
            <div className="dialog" role="dialog" aria-label={t('cashier.promo.title')}
                 data-testid="promo-dialog">
              <h2>{t('cashier.promo.title')}</h2>
              <label>
                {t('cashier.promo.code')}
                <input
                  data-testid="promo-kode"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); }}
                  /* ⛔ Mengganti kodenya membatalkan persetujuan yang sudah diminta — PIN penyelia
                     untuk promo A tak boleh terbawa ke promo B. */
                  onKeyDown={() => { if (approvalNeeded !== null) setApprovalNeeded(null); }}
                />
              </label>

              {/*
                ⭐⭐ **Kotak persetujuan penyelia** — muncul hanya sesudah server memintanya.
                Menyebut ANGKA yang disetujui, bukan cuma "butuh persetujuan".
              */}
              {approvalNeeded !== null && (
                <div className="dialog-note" data-testid="promo-persetujuan">
                  <p>{t('cashier.promo.needsApproval', {
                    amount: formatRupiah(approvalNeeded.discountTotal),
                  })}</p>
                  <label>
                    {t('cashier.promo.approverCode')}
                    <input
                      data-testid="promo-penyelia-kode"
                      value={approvalCode}
                      /* ⚠️ Kode staf disamakan huruf besarnya di layar — server memang tak peduli,
                         tapi kasir yang mengetik `spv1` lalu ditolak akan mengira PIN-nya salah. */
                      onChange={(e) => { setApprovalCode(e.target.value.toUpperCase()); }}
                    />
                  </label>
                  <label>
                    PIN
                    <input
                      type="password"
                      inputMode="numeric"
                      data-testid="promo-penyelia-pin"
                      value={approvalPin}
                      onChange={(e) => { setApprovalPin(e.target.value); }}
                    />
                  </label>
                </div>
              )}

              <div className="dialog-actions">
                <button type="button" data-testid="promo-batal"
                        onClick={() => {
                          setPromoDialog(false); setPromoCode('');
                          setApprovalNeeded(null); setApprovalCode(''); setApprovalPin('');
                        }}>
                  {t('cashier.promo.cancel')}
                </button>
                <button
                  type="button"
                  className="action-primary"
                  data-testid="promo-terapkan"
                  disabled={
                    promoCode.trim() === '' || busy ||
                    /* Saat persetujuan diminta, tombolnya menunggu KEDUANYA terisi. */
                    (approvalNeeded !== null && (approvalCode.trim() === '' || approvalPin.length < 4))
                  }
                  onClick={() => void applyPromo()}
                >
                  {approvalNeeded === null ? t('cashier.promo.apply') : t('cashier.promo.approveApply')}
                </button>
              </div>
            </div>
          )}

          {/*
            ⛔⛔ **Panel bayar hanya untuk yang berizin `payment:take`.**

            Servernya memang sudah menolak (rute `tenders` & `close` menuntut izin itu sejak peran
            pelayan ada) — tapi sampai sekarang layar tetap MENAMPILKAN tombolnya. Jadi pelayan yang
            terlanjur sampai ke layar kasir menekan "Bayar", lalu dapat 403. Servernya benar; yang
            bohong duluan layarnya.

            ⚠️ Ini sopan santun antarmuka, BUKAN kontrol — kontrolnya tetap di server. Menyembunyikan
            tombol tak pernah menghentikan siapa pun yang memanggil API langsung.

            ⭐ Sumbu PERANGKAT (`cashier` vs `pos_terminal`, K20) tak perlu diperiksa di sini: ia
            ditegakkan saat sesi kas dibuka, dan tanpa sesi kas layar ini memang tak pernah tampil.
          */}
          {!hasPermission('payment:take') ? (
            <p className="warn" data-testid="tender-no-permission">{t('cashier.pay.noPermission')}</p>
          ) : (
          <TenderPanel
            discountTotal={bill.discountTotal}
            grandTotal={bill.grandTotal}
            methods={paymentMethods}
            paidAmount={paidAmount}
            busy={busy}
            onPay={(m, j, r, d, e) => void pay(m, j, r, d, e)}
            onClose={() => void close()}
            /*
             * ⭐⭐⭐ **Batal** — kembali menyunting pesanan. ⛔ Bukan void: barisnya utuh, cuma
             * hitungannya yang dibatalkan (server: `POST /orders/:id/cancel-bill`).
             * ⚠️ Tier tablet dikecualikan: `orderId` di sana tak pernah hidup di server (K13), jadi
             * membatalkan cukup membuang bill di layar.
             */
            onCancelBill={() => void batalTagihan()}
          />
          )}
          {/*
            ⭐⭐ **Cetak TAGIHAN — tanpa izin `payment:take`.** Pelayan yang tak boleh menerima uang
            tetap boleh membawa tagihan ke meja; itu justru pekerjaannya. Menaruh tombol ini di
            dalam cabang `payment:take` akan mengunci pelayan dari satu-satunya cetakan yang memang
            miliknya.
            ⛔ Tier tablet dikecualikan: `orderId` di sana tak pernah hidup di server (K13).
          */}
          {!tablet && orderId !== null && (
            <PrintButton orderId={orderId} docType="bill" deviceCode={deviceCode} />
          )}
          </>
        )}

        {done && (
          <>
            <p className="done" data-testid="done">{t('cashier.pay.settled')}</p>
            {/*
              ⭐⭐ **Hasil cetak OTOMATIS** (Imron, 2026-08-25) — lihat `cetakStruk`.

              ⚠️ Yang tampil di sini bukan "berhasil/gagal". Keadaan tengahnya — *tercatat, tapi
              kertas belum keluar* — adalah yang paling sering terjadi di lapangan, dan kasir yang
              tak bisa membedakannya akan menekan Cetak berulang untuk kertas yang tak pernah ada.
            */}
            {autoPrint.kind === 'ok' && (
              <p className="ok" data-testid="auto-print-ok">{t('cashier.print.sent')}</p>
            )}
            {autoPrint.kind === 'warn' && (
              <p className="warn" data-testid="auto-print-warn">
                {t('cashier.print.recordedNotPrinted')} {autoPrint.message}
              </p>
            )}
            {/*
              ⭐ Tombolnya TETAP ada — sekarang sebagai jalan kedua, bukan satu-satunya.
              ⛔ Menghapusnya karena "sudah otomatis" berarti struk yang gagal keluar tak punya
              jalan keluar sama sekali, justru di keadaan yang paling butuh.
            */}
            {lastPaidId !== null && (
              <PrintButton orderId={lastPaidId} docType="receipt" deviceCode={deviceCode} />
            )}
            {/*
              ⭐⭐ **Buka kembali berdiri TEPAT di sini** — di samping "Lunas", selama tamunya
              masih di depan meja. Begitu kasir berpindah ke pesanan berikutnya, `lastPaidId`
              hilang dan jawabannya kembali jadi refund, sebagaimana mestinya.

              ⛔ Bukan daftar bill lunas yang bisa ditelusuri: itu mengubah "perbaiki salah ketik
              yang barusan" jadi wewenang membongkar penjualan siapa pun, kapan pun.
            */}
            {/*
              ⭐⭐⭐ **REFUND** — dan ia sengaja tak dibatasi ke bill yang barusan saja seperti
              buka-kembali. Alasannya berbeda: buka-kembali membongkar penjualan (jadi wewenangnya
              harus sempit dan sesaat), refund menerbitkan dokumen BARU yang tak menyentuh
              penjualan aslinya sama sekali — dan tamu yang komplain sering datang beberapa jam
              kemudian.

              ⚠️ Untuk sekarang jalur masuknya tetap dari bill yang baru lunas: mencari bill lama
              butuh layar pencarian yang belum ada, dan tombol yang menuju layar kosong lebih buruk
              daripada tombol yang belum ada.
            */}
            {lastPaidId !== null && hasPermission('order:refund') && (
              <button
                type="button"
                className="action-secondary"
                data-testid="refund-open"
                onClick={() => { setRefundFor(lastPaidId); }}
              >
                {t('cashier.refund.button')}
              </button>
            )}
            {refundInfo !== null && (
              <p className="done" data-testid="refund-done">{refundInfo}</p>
            )}
            {lastPaidId !== null && (
              <ReopenButton
                orderId={lastPaidId}
                onReopened={async (id) => {
                  /*
                   * ⭐ Bill dipulihkan ke layar supaya kasir bisa langsung membetulkannya. Tanpa
                   * ini ia terbuka di server tapi tak terlihat di mana pun — dan bill `billed`
                   * dengan seluruh tender terbalik yang tak pernah ditutup adalah cara paling
                   * senyap untuk kehilangan satu penjualan.
                   */
                  setDone(false);
                  setLastPaidId(null);
                  setOrderId(id);
                  props.onOrderChanged?.(id);
                  setPaidAmount('0.0000');
                  try {
                    setBill(await api.bill(id, {
                      actor: pelaku(),
                    }));
                  } catch (e) {
                    handleError(e, () => undefined);
                  }
                }}
              />
            )}
          </>
        )}
      </aside>

      {/* ⭐ Dialog paksa-buka hidup di LUAR panel: ia bisa dipicu aksi mana pun di layar ini. */}
      {holdConflict !== null && (
        <TakeOverDialog
          conflict={holdConflict}
          deviceCode={deviceCode}
          onClose={() => setHoldConflict(null)}
        />
      )}
    </div>
  );
}
